/**
 * Layer management — creates and manages visualization layers.
 *
 * Rendering approach:
 *   Polygon fills/borders: MapLibre native fill + line layers (zero tessellation artifacts on globe)
 *   Point markers:         Deck.gl ScatterplotLayer via MapboxOverlay
 *
 * MapLibre's globe projection handles polygon tessellation in the vertex shader,
 * so we no longer need BinaryPolygonLayer or pre-triangulated meshes.
 *
 * Property lookup is case-insensitive to handle both standard (iso3, name_en, population)
 * and legacy Natural Earth/GeoParquet names (ISO_A3, NAME, pop_est).
 */
import maplibregl from 'maplibre-gl'
import { ScatterplotLayer } from '@deck.gl/layers'
import { createColorScale, getDataExtent, getUniqueValues } from '../utils/color-scales.js'
import { MARKER_CLASSES, MARKER_BREAKS } from './globe.js'
import {
  aggregateToH3,
  createH3Layer,
  buildH3GeoJSON,
  buildH3LegendConfig,
  zoomToResolution,
  smartResolution,
} from '../layers/h3-layer.js'
import { H3HexagonLayer } from '@deck.gl/geo-layers'
import { cellToBoundary } from 'h3-js'
import { ChoroplethStyleManager } from '../layers/choropleth-style-manager.js'
import { DESERT_COUNTRIES, isDesertCountry } from '../config.js'
import {
  createStack,
  registerEntry,
  resolveEntry,
  removeEntry,
  listEntries,
  clearEntries,
  stackNameForStep,
} from './layer-stack.js'
// Note: H3 rendering now uses Deck.gl H3HexagonLayer exclusively (via createH3Layer).
// Color ramp computation and cellToBoundary are handled inside h3-layer.js.

// CL-165 (H3.2): MapLibre opacity paint property per layer type, used by
// setStackOpacity to adjust any registered layer generically.
const OPACITY_PROP_BY_TYPE = {
  fill: ['fill-opacity'],
  line: ['line-opacity'],
  circle: ['circle-opacity'],
  symbol: ['icon-opacity', 'text-opacity'],
  raster: ['raster-opacity'],
  heatmap: ['heatmap-opacity'],
  'fill-extrusion': ['fill-extrusion-opacity'],
}

/**
 * Get a property value from a feature, case-insensitively.
 */
// Alias chains for attribute name mismatches between
// Natural Earth properties and SQL-aliased DuckDB output columns.
const PROP_ALIASES = {
  pop_est: ['population', 'POP_EST'],
  POP_EST: ['population', 'pop_est'],
  population: ['pop_est', 'POP_EST'],
  gdp_md: ['GDP_MD', 'gdp', 'gdp_usd'],
  GDP_MD: ['gdp_md', 'gdp', 'gdp_usd'],
  name_en: ['name', 'NAME'],
  name: ['name_en', 'NAME'],
  pop_density: ['density_per_km2', 'pop_density_km2'],
}

function getProp(feature, key) {
  const props = feature.properties
  if (!props) return undefined
  if (props[key] !== undefined) return props[key]
  const lower = key.toLowerCase()
  if (props[lower] !== undefined) return props[lower]
  const upper = key.toUpperCase()
  if (props[upper] !== undefined) return props[upper]
  // Try known aliases
  const aliases = PROP_ALIASES[key] || PROP_ALIASES[lower]
  if (aliases) {
    for (const alt of aliases) {
      if (props[alt] !== undefined) return props[alt]
    }
  }
  return undefined
}

/**
 * Convert [r, g, b] to hex color string (#RRGGBB).
 */
function rgbToHex([r, g, b]) {
  return '#' + [r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')
}

/**
 * Convert hex color (#RRGGBB) to RGBA array [r, g, b, a].
 */
function hexToRgba(hex) {
  const m = hex.replace('#', '').match(/.{2}/g)
  return [...m.map((c) => parseInt(c, 16)), 255]
}

/**
 * Build MapLibre fill paint for data-driven _fill property (hex color).
 * Opacity is set separately per-layer for reliable rendering.
 */
function makeFillPaint(opacity = 0.6) {
  return {
    'fill-color': ['coalesce', ['get', '_fill'], '#808080'],
    'fill-opacity': opacity,
  }
}

class LayerManager {
  constructor(globe) {
    this.globe = globe
    this._deckLayers = new Map() // ScatterplotLayers for Deck.gl overlay
    this.legendCallback = null
    this.statusCallback = null
    this.choroplethStyleManager = new ChoroplethStyleManager(globe, this)
    /** @type {{ attribute: string, displayName: string } | null} */
    this._activeChoropleth = null
    // Phase 2 (compound stacking): when true, clearLayers() is a no-op so an
    // OVERLAY render (markers, ellipse, isochrone, …) preserves the active base
    // choropleth and existing overlays instead of wiping them. Default off, so
    // single-command behavior is unchanged. The compound executor toggles this
    // around overlay steps via beginOverlay()/endOverlay().
    this._overlayMode = false
    // CL-165 (roadmap H3.2): the pedagogical layer stack — named,
    // transparency-controlled, toggleable entries. Populated by the pipeline
    // executor via snapshot/diff around each op; controlled by the
    // layer_control op ("hide the population layer").
    this.stack = createStack()
    // CL-173 (roadmap H3.2b): stack mode — the student-facing persistent
    // sibling of _overlayMode. While on, clearLayers() is gated so renders
    // accumulate, and the layer sidebar shows/controls the stack. Toggled by
    // the header Layers button, voice ("turn on stack mode"), or a lesson's
    // top-level layer_mode:"stack" flag.
    this._stackMode = false
    /** @type {Array<() => void>} */
    this._stackListeners = []
  }

  // ── Pedagogical layer stack (CL-165, roadmap H3.2) ─────────

  /** CL-173: subscribe to stack changes (sidebar re-render). Returns unsubscribe. */
  onStackChanged(cb) {
    if (typeof cb !== 'function') return () => {}
    this._stackListeners.push(cb)
    return () => {
      this._stackListeners = this._stackListeners.filter((f) => f !== cb)
    }
  }

  _notifyStackChanged() {
    for (const cb of this._stackListeners) {
      try {
        cb()
      } catch {
        /* listener errors must never break rendering */
      }
    }
  }

  /** CL-173 (H3.2b): enter/leave persistent stack mode. */
  setStackMode(on) {
    this._stackMode = !!on
    this._notifyStackChanged()
  }

  isStackMode() {
    return this._stackMode
  }

  /** Snapshot current MapLibre + Deck layer ids (for executor diffing). */
  snapshotLayerIds() {
    let mapIds = []
    try {
      mapIds = (this.globe.map?.getStyle()?.layers || []).map((l) => l.id)
    } catch {
      /* style not loaded yet — empty snapshot is fine */
    }
    return { map: new Set(mapIds), deck: new Set(this._deckLayers.keys()) }
  }

  /**
   * Register whatever a pipeline step just added as a named stack entry.
   * Called by the executor after each op; a no-op when nothing was added.
   */
  registerStackFromDiff(step, before) {
    try {
      if (!before || step?.op === 'layer_control') return null
      const now = this.snapshotLayerIds()
      const mapLayerIds = [...now.map].filter((id) => !before.map.has(id))
      const deckIds = [...now.deck].filter((id) => !before.deck.has(id))
      if (mapLayerIds.length === 0 && deckIds.length === 0) return null
      const name = stackNameForStep(step)
      if (!name) return null
      // CL-173: when the cap evicts an entry, remove its layers from the map
      // too — in stack mode nothing else would ever clear them.
      const registered = registerEntry(
        this.stack,
        { name, op: step.op, mapLayerIds, deckIds },
        (evicted) => this.removeStackEntry(evicted)
      )
      if (registered) this._notifyStackChanged()
      return registered
    } catch (err) {
      console.warn('[LayerManager] stack registration failed (ignored):', err.message)
      return null
    }
  }

  /** Resolve a student's layer reference ("the population layer") or null. */
  resolveStackEntry(query) {
    return resolveEntry(this.stack, query)
  }

  /** Names + visibility of everything on the stack, newest first. */
  listStack() {
    return listEntries(this.stack)
  }

  /** Show/hide every layer in a stack entry (MapLibre + Deck). */
  setStackVisibility(entry, visible) {
    if (!entry) return false
    const map = this.globe.map
    for (const id of entry.mapLayerIds) {
      try {
        if (map?.getLayer(id)) map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none')
      } catch {
        /* layer replaced since registration — skip */
      }
    }
    let deckChanged = false
    for (const id of entry.deckIds) {
      const layer = this._deckLayers.get(id)
      if (layer) {
        this._deckLayers.set(id, layer.clone({ visible }))
        deckChanged = true
      }
    }
    if (deckChanged) this._updateDeckOverlay()
    entry.visible = visible
    this._notifyStackChanged()
    return true
  }

  /** Set opacity (0–1) for every layer in a stack entry (MapLibre + Deck). */
  setStackOpacity(entry, opacity) {
    if (!entry) return false
    const value = Math.max(0, Math.min(1, Number(opacity)))
    if (!Number.isFinite(value)) return false
    const map = this.globe.map
    for (const id of entry.mapLayerIds) {
      try {
        const layer = map?.getLayer(id)
        if (!layer) continue
        for (const prop of OPACITY_PROP_BY_TYPE[layer.type] || []) {
          map.setPaintProperty(id, prop, value)
        }
      } catch {
        /* layer replaced since registration — skip */
      }
    }
    let deckChanged = false
    for (const id of entry.deckIds) {
      const layer = this._deckLayers.get(id)
      if (layer) {
        this._deckLayers.set(id, layer.clone({ opacity: value }))
        deckChanged = true
      }
    }
    if (deckChanged) this._updateDeckOverlay()
    entry.opacity = value
    return true
  }

  /** Remove one named entry's layers from the map and the stack. */
  removeStackEntry(entry) {
    if (!entry) return false
    const map = this.globe.map
    for (const id of entry.mapLayerIds) {
      try {
        if (map?.getLayer(id)) map.removeLayer(id)
      } catch {
        /* already gone */
      }
    }
    let deckChanged = false
    for (const id of entry.deckIds) {
      if (this._deckLayers.delete(id)) deckChanged = true
    }
    if (deckChanged) this._updateDeckOverlay()
    removeEntry(this.stack, entry)
    this._notifyStackChanged()
    return true
  }

  /**
   * Enter overlay (stacking) mode — subsequent renders ADD to the map without
   * clearing the active base layer. Phase 2.
   */
  beginOverlay() {
    this._overlayMode = true
  }

  /** Leave overlay mode; renders return to clear-and-replace. Phase 2. */
  endOverlay() {
    this._overlayMode = false
  }

  /**
   * Register a callback for legend updates
   */
  onLegendUpdate(callback) {
    this.legendCallback = callback
  }

  /**
   * Register a callback for status updates
   */
  onStatusUpdate(callback) {
    this.statusCallback = callback
  }

  /**
   * Clear all data layers (native polygon + Deck.gl overlay)
   * @param {{ force?: boolean }} [opts] — force bypasses overlay AND stack
   *   mode gates (explicit user reset must always work; CL-173).
   */
  clearLayers(opts = {}) {
    // Phase 2 stacking: in overlay mode, preserve the active base + overlays so
    // an additive step (markers, ellipse, isochrone) stacks instead of wiping.
    // CL-173 (H3.2b): stack mode gates the same way, persistently — ops keep
    // calling clearLayers() unchanged; the mode decides whether it acts.
    if ((this._overlayMode || this._stackMode) && !opts.force) return
    // CL-165: a full clear resets the pedagogical stack too.
    clearEntries(this.stack)
    this._notifyStackChanged()
    this.choroplethStyleManager.clear()
    this._deckLayers.clear()
    this._activeChoropleth = null
    this.globe._activeChoropleth = null
    this.globe._customPopupBuilder = null
    // Clean up H3 click handler and extrusion layer
    if (this._h3ClickHandler && this.globe.map) {
      try {
        this.globe.map.off('click', 'data-h3-density-fill', this._h3ClickHandler)
      } catch (e) {}
      this._h3ClickHandler = null
    }
    // Clean up heritage click handler
    if (this._heritageClickHandler && this.globe.map) {
      try {
        this.globe.map.off('click', 'data-heritage-circle', this._heritageClickHandler)
      } catch (e) {}
      this._heritageClickHandler = null
    }
    // Clean up languages click handler
    if (this._languageClickHandler && this.globe.map) {
      try {
        this.globe.map.off('click', 'data-languages-circle', this._languageClickHandler)
      } catch (e) {}
      this._languageClickHandler = null
    }
    // Clean up people groups (Joshua Project) click/dblclick handlers
    if (this._peopleGroupClusterClickHandler && this.globe.map) {
      try {
        this.globe.map.off(
          'click',
          'data-people-groups-clusters',
          this._peopleGroupClusterClickHandler
        )
      } catch (e) {}
      this._peopleGroupClusterClickHandler = null
    }
    if (this._peopleGroupPointClickHandler && this.globe.map) {
      try {
        this.globe.map.off('click', 'data-people-groups-circle', this._peopleGroupPointClickHandler)
      } catch (e) {}
      this._peopleGroupPointClickHandler = null
    }
    if (this._peopleGroupDblClickHandler && this.globe.map) {
      try {
        this.globe.map.off(
          'dblclick',
          'data-people-groups-clusters',
          this._peopleGroupDblClickHandler
        )
      } catch (e) {}
      this._peopleGroupDblClickHandler = null
    }
    this._pgFeatures = null
    this._pgSchemeIndex = 0
    // Clean up the native H3 density layers (globe-safe render path)
    for (const id of ['data-h3-density-line', 'data-h3-density-fill', 'data-h3-density-3d']) {
      try {
        if (this.globe.map?.getLayer(id)) this.globe.map.removeLayer(id)
      } catch (e) {}
    }
    try {
      if (this.globe.map?.getSource('data-h3-density-src')) {
        this.globe.map.removeSource('data-h3-density-src')
      }
    } catch (e) {}
    // Clean up H3 extrusion layers
    try {
      if (this.globe.map?.getLayer('data-h3-ext')) this.globe.map.removeLayer('data-h3-ext')
    } catch (e) {}
    try {
      if (this.globe.map?.getLayer('data-h3-ext-flat'))
        this.globe.map.removeLayer('data-h3-ext-flat')
    } catch (e) {}
    try {
      if (this.globe.map?.getSource('data-h3-ext-src'))
        this.globe.map.removeSource('data-h3-ext-src')
    } catch (e) {}
    this._h3State = null
    this.globe.updateLayers([])
    this.globe.clearNativeLayers('data-')
    this.legendCallback?.({ visible: false })
    this._onH3ModeChange?.(null)
    // Clear Deck.gl layers and tooltip handler
    if (this.globe.deckOverlay) {
      this.globe.deckOverlay.setProps({ layers: [], getTooltip: null })
    }
  }

  /**
   * Update the Deck.gl overlay with current point layers.
   */
  _updateDeckOverlay() {
    this.globe.updateLayers([...this._deckLayers.values()])
  }

  /**
   * Enrich GeoJSON features with pre-computed _fill hex colors.
   * @param {object} geojson - GeoJSON FeatureCollection
   * @param {Function|Array} getFillColor - color accessor returning [r,g,b,...] or static RGBA array
   * @returns {object} Enriched GeoJSON FeatureCollection
   */
  _enrichFeatures(geojson, getFillColor) {
    return {
      type: 'FeatureCollection',
      features: geojson.features.map((f) => {
        const rgba = typeof getFillColor === 'function' ? getFillColor(f) : getFillColor
        return {
          ...f,
          properties: {
            ...f.properties,
            _fill: rgbToHex(rgba),
          },
        }
      }),
    }
  }

  // ── Polygon visualizations (MapLibre native) ──────────────

  /**
   * Highlight a single feature (country outline, e.g. "Go to France")
   */
  highlightFeature(geojson) {
    this.clearLayers()

    const desert = isDesertCountry(geojson)
    const fillColor = desert ? [255, 20, 147, 160] : [245, 166, 35, 160]
    const lineCSS = desert ? 'rgba(255,20,147,1)' : 'rgba(255,200,60,1)'

    const enriched = this._enrichFeatures(geojson, () => fillColor)

    this.globe.addPolygonLayer('data-highlight', enriched, {
      fill: makeFillPaint(0.63),
      line: {
        'line-color': lineCSS,
        'line-width': 2,
      },
    })
  }

  /**
   * Show all features in a dataset with categorical colors
   */
  showDataset(geojson, options = {}) {
    this.clearLayers()

    const attribute = options.attribute || 'NAME'
    const uniqueVals = getUniqueValues(geojson.features, attribute)
    const colorScale = createColorScale(null, 'categorical', uniqueVals)

    const enriched = this._enrichFeatures(geojson, (f) => {
      const val = getProp(f, attribute)
      const c = colorScale.getColor(val)
      return [c[0], c[1], c[2], 120]
    })

    this.globe.addPolygonLayer('data-dataset', enriched, {
      fill: makeFillPaint(0.63),
      line: {
        'line-color': 'rgba(255,255,255,0.7)',
        'line-width': 1,
      },
    })

    if (uniqueVals.length <= 10) {
      this.legendCallback?.({
        visible: true,
        title: attribute,
        type: 'categorical',
        stops: colorScale.getLegendStops(),
      })
    }
  }

  /**
   * Show a choropleth visualization
   */
  showChoropleth(geojson, attribute, options = {}) {
    console.log(
      `[LayerManager] showChoropleth: ${geojson?.features?.length} features, attribute="${attribute}"`
    )
    this.clearLayers()

    // Store active choropleth info so popups can display the attribute value
    this._activeChoropleth = {
      attribute,
      displayName: formatAttributeName(attribute),
    }
    this.globe._activeChoropleth = this._activeChoropleth

    const rampName = options.colorRamp || 'YlOrRd'
    const scheme = options.colorScheme || 'sequential'

    let colorScale
    if (scheme === 'categorical') {
      const uniqueVals = getUniqueValues(geojson.features, attribute)
      colorScale = createColorScale(rampName, 'categorical', uniqueVals)
    } else {
      const extent = getDataExtent(geojson.features, attribute)

      if (extent[0] === 0 && extent[1] === 1) {
        console.warn(
          `[layers] Attribute "${attribute}" appears non-numeric, falling back to categorical display`
        )
        return this.showDataset(geojson, { attribute })
      }

      // Extract all numeric values for quantile-based breaks
      const values = geojson.features
        .map((f) => getProp(f, attribute))
        .filter((v) => v != null && !isNaN(v))
        .map(Number)

      colorScale = createColorScale(rampName, scheme, extent, { values })
    }

    const enriched = this._enrichFeatures(geojson, (f) => {
      const val = getProp(f, attribute)
      const c = colorScale.getColor(val)
      return [c[0], c[1], c[2], 160]
    })

    // Debug: log a sample feature's _fill color
    if (enriched.features.length > 0) {
      const sample = enriched.features[0]
      console.log(
        `[LayerManager] Choropleth sample: ${sample.properties.NAME || sample.properties.name} → _fill=${sample.properties._fill}, geometry=${sample.geometry?.type}`
      )
    }

    const fillPaint = makeFillPaint(0.63)
    const linePaint = {
      'line-color': 'rgba(255,255,255,0.7)',
      'line-width': 1,
    }

    const legendConfig = {
      visible: true,
      title: formatAttributeName(attribute),
      type: scheme === 'categorical' ? 'categorical' : 'classified',
      stops: colorScale.getLegendStops(),
      domain: colorScale.domain,
      ramp: rampName,
    }

    // Hand off to ChoroplethStyleManager — it renders using the current mode
    // and emits its own tier legend via onLegendUpdate.
    try {
      if (scheme === 'categorical') {
        // A categorical attribute holds strings, and setData()'s classification
        // path coerces every value with Number() — producing NaN for all of
        // them, empty break computation, and a map with no colour at all.
        // The features were already coloured above, so hand the manager
        // pre-classified geometry and the categorical key we just built.
        this.choroplethStyleManager.setPreclassifiedData({
          enrichedGeojson: enriched,
          attribute: null,
          linePaint,
          legendConfig,
        })
      } else {
        this.choroplethStyleManager.setData(
          {
            enrichedGeojson: enriched,
            rawGeojson: geojson,
            attribute,
            colorRamp: rampName,
            colorScheme: scheme,
            legendConfig,
            fillPaint,
            linePaint,
            adm: options.adm || 'ADM0',
          },
          options.precomputedBreaks
        )
      }
    } catch (err) {
      console.error('[LayerManager] ChoroplethStyleManager.setData failed:', err)
      // Surface error for debugging (survives production console stripping)
      if (typeof window !== 'undefined') {
        window.__earthvoice_lastError = {
          op: 'showChoropleth',
          attribute,
          featureCount: enriched?.features?.length,
          error: err.message,
          stack: err.stack,
          timestamp: Date.now(),
        }
      }
      // Fall back to basic rendering without classification
      this.globe.addPolygonLayer('data-choropleth', enriched, {
        fill: {
          'fill-color': ['coalesce', ['get', '_fill'], '#787878'],
          'fill-opacity': 0.75,
        },
        line: {
          'line-color': 'rgba(255,255,255,0.7)',
          'line-width': 1,
        },
      })
    }

    // MapLibre globe-projection quirk: a fill layer mounted while a camera
    // animation (flyTo) is in flight can fail to composite — the layer exists,
    // queryRenderedFeatures sees it, but pixels stay unpainted until the next
    // camera change (observed live 2026-06-12: suitability choropleth
    // invisible until a manual jumpTo). Nudge the camera by a no-op jump once
    // the map settles so the layer always composites.
    const map = this.globe?.map
    if (map) {
      map.once('idle', () => {
        try {
          map.jumpTo({ center: map.getCenter() })
          map.triggerRepaint()
        } catch {
          /* never let a repaint nudge break the render path */
        }
      })
    }
  }

  /**
   * Show a bivariate choropleth — two variables on a 3×3 color grid.
   * Classifies each variable into 3 quantile classes, assigns a hex color
   * from a 3×3 Joshua Stevens–inspired palette per feature, renders via MapLibre.
   */
  showBivariateChoropleth(geojson, attribute1, attribute2) {
    console.log(
      `[LayerManager] showBivariateChoropleth: ${geojson?.features?.length} features, "${attribute1}" × "${attribute2}"`
    )
    this.clearLayers()

    // ── 3×3 palette (Joshua Stevens teal-rose) ──
    // Rows = attribute2 (low→high bottom→top), Columns = attribute1 (low→high left→right)
    const PALETTE = [
      ['#e8e8e8', '#b5c0da', '#6c83b5'], // row 0: attr2 low
      ['#b8d6be', '#90b2b3', '#567994'], // row 1: attr2 mid
      ['#73ae80', '#5a9178', '#2a5a5b'], // row 2: attr2 high
    ]
    const NO_DATA_COLOR = '#d9d9d9'

    // ── Extract numeric values for classification ──
    const vals1 = []
    const vals2 = []
    for (const f of geojson.features) {
      const v1 = getProp(f, attribute1)
      const v2 = getProp(f, attribute2)
      if (v1 != null && !isNaN(v1)) vals1.push(Number(v1))
      if (v2 != null && !isNaN(v2)) vals2.push(Number(v2))
    }

    if (vals1.length < 3 || vals2.length < 3) {
      console.warn('[LayerManager] Not enough data for bivariate classification')
      return this.showChoropleth(geojson, attribute1, {})
    }

    // ── Quantile breaks (terciles) ──
    const quantileBreaks = (values) => {
      const sorted = [...values].sort((a, b) => a - b)
      return [sorted[Math.floor(sorted.length / 3)], sorted[Math.floor((sorted.length * 2) / 3)]]
    }
    const breaks1 = quantileBreaks(vals1)
    const breaks2 = quantileBreaks(vals2)

    const classify = (val, breaks) => {
      if (val <= breaks[0]) return 0
      if (val <= breaks[1]) return 1
      return 2
    }

    // ── Assign bivariate color per feature ──
    const enriched = JSON.parse(JSON.stringify(geojson))
    for (const f of enriched.features) {
      const v1 = getProp(f, attribute1)
      const v2 = getProp(f, attribute2)
      if (v1 == null || v2 == null || isNaN(v1) || isNaN(v2)) {
        f.properties._fill = NO_DATA_COLOR
        f.properties.biv_class = 'ND'
      } else {
        const c1 = classify(Number(v1), breaks1)
        const c2 = classify(Number(v2), breaks2)
        f.properties._fill = PALETTE[c2][c1]
        f.properties.biv_class = `${c2}-${c1}`
      }
    }

    // ── Render via MapLibre native fill ──
    // Use base ID 'data-bivariate' so _removeNativeSync finds ${id}-fill, ${id}-line, ${id}-src
    const baseId = 'data-bivariate'
    const sourceId = `${baseId}-src`
    const fillId = `${baseId}-fill`
    const lineId = `${baseId}-line`

    // Remove old layers/sources if they exist
    this.globe._removeNativeSync?.(baseId)

    this.globe.map.addSource(sourceId, { type: 'geojson', data: enriched })
    this.globe.map.addLayer({
      id: fillId,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': ['coalesce', ['get', '_fill'], NO_DATA_COLOR],
        'fill-opacity': 0.75,
      },
    })
    this.globe.map.addLayer({
      id: lineId,
      type: 'line',
      source: sourceId,
      paint: { 'line-color': 'rgba(255,255,255,0.5)', 'line-width': 0.8 },
    })

    // Register base ID with globe's native layer tracking so clearNativeLayers('data-') cleans them up
    this.globe._nativeLayerIds.add(baseId)

    // ── Bivariate legend ──
    const label1 = formatAttributeName(attribute1)
    const label2 = formatAttributeName(attribute2)
    this.legendCallback?.({
      visible: true,
      type: 'bivariate',
      title: `${label1} × ${label2}`,
      palette: PALETTE,
      attribute1: label1,
      attribute2: label2,
      noDataColor: NO_DATA_COLOR,
    })
  }

  /**
   * Show filtered results (highlight matching features)
   */
  showFilteredResults(geojson, vizType, options = {}) {
    if (vizType === 'proportional-symbol') {
      this.showProportionalSymbols(geojson, options.attribute, options)
    } else if (vizType === 'choropleth') {
      this.showChoropleth(geojson, options.attribute, options)
    } else {
      this.clearLayers()

      const desert = isDesertCountry(geojson)
      const fillColor = desert ? [255, 20, 147, 140] : [245, 166, 35, 140]
      const lineCSS = desert ? 'rgba(255,20,147,0.9)' : 'rgba(255,200,60,0.9)'

      const enriched = this._enrichFeatures(geojson, () => fillColor)

      this.globe.addPolygonLayer('data-filter', enriched, {
        fill: makeFillPaint(0.63),
        line: {
          'line-color': lineCSS,
          'line-width': 2,
        },
      })
    }
  }

  /**
   * Show divisions as a choropleth colored by a data value (e.g., POI count).
   */
  showDivisionChoropleth({ features, attribute = 'poi_count', label = 'Count' }) {
    this.clearLayers()

    const fc = { type: 'FeatureCollection', features }
    const extent = getDataExtent(features, attribute)
    const colorScale = createColorScale('YlOrRd', 'sequential', extent)

    const enriched = this._enrichFeatures(fc, (f) => {
      const val = f.properties[attribute]
      const c = colorScale.getColor(val)
      return [c[0], c[1], c[2], 170]
    })

    this._activeChoropleth = { attribute, displayName: label }
    this.globe._activeChoropleth = this._activeChoropleth

    const legendConfig = {
      visible: true,
      title: label,
      type: 'classified',
      stops: colorScale.getLegendStops(),
      domain: extent,
      ramp: 'YlOrRd',
    }

    // Route through ChoroplethStyleManager for extrusion + mode switcher support
    this.choroplethStyleManager.setPreclassifiedData({
      enrichedGeojson: enriched,
      attribute,
      linePaint: { 'line-color': 'rgba(255,255,255,0.78)', 'line-width': 1.5 },
      legendConfig,
    })
  }

  // ── Point visualizations (Deck.gl ScatterplotLayer) ────────

  /**
   * Classify a population value into a marker class index (0–4).
   */
  _getMarkerClass(value) {
    if (value == null || isNaN(value)) return 0
    for (let i = 0; i < MARKER_BREAKS.length; i++) {
      if (value < MARKER_BREAKS[i]) return i
    }
    return MARKER_BREAKS.length // largest class
  }

  /**
   * Show proportional symbols as LeafMap-style teardrop pin markers.
   * Population determines both marker size and color.
   */
  showProportionalSymbols(geojson, attribute, options = {}) {
    this.clearLayers()

    // Convert features to Point GeoJSON with marker classification properties
    const pointFeatures = geojson.features
      .map((f) => {
        let coords
        if (f.geometry?.type === 'Point') {
          coords = f.geometry.coordinates
        } else {
          // Use LONGITUDE/LATITUDE properties as fallback for non-point geometries
          const lng = getProp(f, 'LONGITUDE') ?? getProp(f, 'longitude')
          const lat = getProp(f, 'LATITUDE') ?? getProp(f, 'latitude')
          if (lng != null && lat != null) {
            coords = [Number(lng), Number(lat)]
          } else {
            return null // skip features with no position
          }
        }

        // Try the specified attribute, then common population field names
        const val = Number(
          getProp(f, attribute) ??
            getProp(f, 'population') ??
            getProp(f, 'pop_max') ??
            getProp(f, 'pop_est') ??
            getProp(f, 'POP_MAX') ??
            getProp(f, 'POP_EST') ??
            0
        )
        const classIdx = this._getMarkerClass(val)
        const mc = MARKER_CLASSES[classIdx]

        // Size multiplier: 0.5 for smallest, 1.0 for largest
        const sizeMultiplier = 0.5 + (classIdx / (MARKER_CLASSES.length - 1)) * 0.5

        return {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: coords },
          properties: {
            ...f.properties,
            _marker: mc.id,
            _markerSize: sizeMultiplier,
            _label: getProp(f, 'name_en') || getProp(f, 'NAME') || getProp(f, 'name') || '',
            _sortKey: -val, // larger cities render on top (lower sort key = higher priority)
          },
        }
      })
      .filter(Boolean)

    // Debug: log a sample of cities with their population values and classifications
    const sample = pointFeatures.slice(0, 10)
    console.log(
      '[LayerManager] City marker samples:',
      sample.map((f) => ({
        name: f.properties._label,
        pop:
          f.properties.population ??
          f.properties.pop_max ??
          f.properties.POP_MAX ??
          f.properties.pop_est,
        marker: f.properties._marker,
        size: f.properties._markerSize,
      }))
    )

    const pointGeoJSON = { type: 'FeatureCollection', features: pointFeatures }

    this.globe.addMarkerLayer('data-markers', pointGeoJSON, {
      allowOverlap: pointFeatures.length <= 50,
    })

    // Build legend with marker colors
    this.legendCallback?.({
      visible: true,
      title: formatAttributeName(attribute),
      type: 'categorical',
      stops: MARKER_CLASSES.map((mc) => ({
        color: hexToRgba(mc.color),
        label: mc.label,
      })),
    })
  }

  /**
   * Show proximity-join results as bright teardrop markers.
   *
   * For count modes, markers are sized and colored by the nearby-count
   * (`options.valueAttr`, e.g. '_nearby') using count-appropriate breaks; for
   * filter mode (no valueAttr) all results render as uniform bright markers.
   * Distinct from showProportionalSymbols, which classifies by population.
   */
  showProximityResults(geojson, options = {}) {
    this.clearLayers()
    const valueAttr = options.valueAttr || null

    // Count → marker class (green → dark red). Filter mode uses one bright class.
    const classForCount = (v) => (v >= 10 ? 4 : v >= 5 ? 3 : v >= 3 ? 2 : v >= 2 ? 1 : 0)
    const FILTER_CLASS = 2 // orange, mid size

    const features = (geojson.features || [])
      .map((f) => {
        const coords =
          f.geometry?.type === 'Point'
            ? f.geometry.coordinates
            : [Number(getProp(f, 'longitude')), Number(getProp(f, 'latitude'))]
        if (!coords || !Number.isFinite(coords[0]) || !Number.isFinite(coords[1])) return null

        const val = valueAttr ? Number(getProp(f, valueAttr) ?? 0) : null
        const classIdx = valueAttr ? classForCount(val) : FILTER_CLASS
        const mc = MARKER_CLASSES[classIdx]
        const sizeMultiplier = 0.5 + (classIdx / (MARKER_CLASSES.length - 1)) * 0.5

        return {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: coords },
          properties: {
            ...f.properties,
            _marker: mc.id,
            _markerSize: sizeMultiplier,
            _label: getProp(f, 'name') || '',
            _sortKey: valueAttr ? -val : 0, // higher counts render on top
          },
        }
      })
      .filter(Boolean)

    this.globe.addMarkerLayer(
      'data-markers',
      { type: 'FeatureCollection', features },
      { allowOverlap: features.length <= 50 }
    )

    if (valueAttr) {
      const labels = ['1', '2', '3 – 4', '5 – 9', '10+']
      this.legendCallback?.({
        visible: true,
        title: 'Nearby count',
        type: 'categorical',
        stops: MARKER_CLASSES.map((mc, i) => ({ color: hexToRgba(mc.color), label: labels[i] })),
      })
    } else {
      this.legendCallback?.({
        visible: true,
        title: options.legendTitle || 'In range',
        type: 'categorical',
        stops: [
          {
            color: hexToRgba(MARKER_CLASSES[FILTER_CLASS].color),
            label: options.legendLabel || 'Within distance',
          },
        ],
      })
    }
  }

  /**
   * Show POI (Point of Interest) results as categorized scatter points.
   */
  showPOIResults(rows, options = {}) {
    this.clearLayers()

    // Convert tabular rows to GeoJSON for MapLibre native circle layer
    const geojson = {
      type: 'FeatureCollection',
      features: rows.map((r) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [r.longitude, r.latitude] },
        properties: {
          name: r.name || 'Unknown',
          category: r.category || '',
          city: r.city || '',
          _color: _rgbaToHex(POI_CATEGORY_COLORS[r.category] || POI_CATEGORY_COLORS.default),
        },
      })),
    }

    this.globe.addPOILayer('data-poi', geojson)

    const categoriesInData = [...new Set(rows.map((r) => r.category))]
    if (categoriesInData.length <= 12) {
      const seen = new Map()
      for (const cat of categoriesInData) {
        const color = POI_CATEGORY_COLORS[cat] || POI_CATEGORY_COLORS.default
        const key = color.join(',')
        if (seen.has(key)) {
          seen.get(key).label += ' / ' + formatCategoryName(cat)
        } else {
          seen.set(key, { label: formatCategoryName(cat), color })
        }
      }
      this.legendCallback?.({
        visible: true,
        title: 'Points of Interest',
        type: 'categorical',
        stops: [...seen.values()],
      })
    }
  }

  // ── Categorical layers (non-gradient) ─────────────────────

  /**
   * Add a curated water layer (oceans, intermittent water, salt flats).
   * Uses raw MapLibre fill+line layers — no choropleth machinery, no
   * tessellation. Each call is idempotent: removes any prior version of
   * the same layerId before adding.
   *
   * Source data: GeoJSON FeatureCollections on R2, see src/ai/ops/water_layer.js.
   *
   * @param {string} layerId   - canonical id e.g. 'data-water-oceans'
   * @param {object} geojson   - FeatureCollection (already filtered if a name was given)
   * @param {string} fillColor - hex e.g. '#1B4F72'
   * @param {string} label     - human-readable legend title e.g. 'Oceans & Seas'
   */
  addWaterLayer(layerId, geojson, fillColor, label) {
    const map = this.globe?.map
    if (!map) {
      console.warn('[layers] addWaterLayer: map not ready')
      return
    }

    const sourceId = `${layerId}-src`
    const fillId = `${layerId}-fill`
    const lineId = `${layerId}-line`

    // Idempotent cleanup
    if (map.getLayer(fillId)) map.removeLayer(fillId)
    if (map.getLayer(lineId)) map.removeLayer(lineId)
    if (map.getSource(sourceId)) map.removeSource(sourceId)

    if (!geojson?.features?.length) {
      console.warn(`[layers] addWaterLayer: empty features for ${layerId}`)
      return
    }

    console.log(`[layers] addWaterLayer ${layerId}: ${geojson.features.length} features`)

    map.addSource(sourceId, { type: 'geojson', data: geojson })
    map.addLayer({
      id: fillId,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': fillColor,
        'fill-opacity': 0.55,
      },
    })
    map.addLayer({
      id: lineId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': fillColor,
        'line-width': 0.6,
        'line-opacity': 0.85,
      },
    })

    this.legendCallback?.({
      visible: true,
      title: label,
      type: 'categorical',
      stops: [{ value: label, color: fillColor, label }],
    })
  }

  /**
   * Remove a curated water layer by id (matches addWaterLayer).
   * @param {string} layerId
   */
  removeWaterLayer(layerId) {
    const map = this.globe?.map
    if (!map) return
    const sourceId = `${layerId}-src`
    const fillId = `${layerId}-fill`
    const lineId = `${layerId}-line`
    if (map.getLayer(fillId)) map.removeLayer(fillId)
    if (map.getLayer(lineId)) map.removeLayer(lineId)
    if (map.getSource(sourceId)) map.removeSource(sourceId)
    this.legendCallback?.({ visible: false })
  }

  /**
   * Add a PMTiles-backed water layer (e.g. lakes & rivers — 2.15M features
   * at zoom 0-8 served as vector tiles via the pmtiles:// protocol that
   * globe.js registers at startup).
   *
   * @param {string} layerId          - canonical id e.g. 'data-water-lakes_rivers'
   * @param {string} pmtilesUrl       - https://...lakes_and_rivers.pmtiles
   * @param {string} sourceLayerName  - the layer name baked into the PMTiles by tippecanoe -l
   * @param {string} fillColor        - hex e.g. '#2E86C1'
   * @param {string} label            - legend title e.g. 'Lakes & Rivers'
   */
  addWaterPMTilesLayer(layerId, pmtilesUrl, sourceLayerName, fillColor, label) {
    const map = this.globe?.map
    if (!map) {
      console.warn('[layers] addWaterPMTilesLayer: map not ready')
      return
    }

    const sourceId = `${layerId}-src`
    const fillId = `${layerId}-fill`
    const lineId = `${layerId}-line`

    // Idempotent cleanup
    if (map.getLayer(fillId)) map.removeLayer(fillId)
    if (map.getLayer(lineId)) map.removeLayer(lineId)
    if (map.getSource(sourceId)) map.removeSource(sourceId)

    const protoUrl = pmtilesUrl.startsWith('pmtiles://') ? pmtilesUrl : `pmtiles://${pmtilesUrl}`

    console.log(`[layers] addWaterPMTilesLayer ${layerId}: ${protoUrl}`)

    map.addSource(sourceId, { type: 'vector', url: protoUrl })
    map.addLayer({
      id: fillId,
      type: 'fill',
      source: sourceId,
      'source-layer': sourceLayerName,
      paint: {
        'fill-color': fillColor,
        'fill-opacity': 0.55,
      },
    })
    map.addLayer({
      id: lineId,
      type: 'line',
      source: sourceId,
      'source-layer': sourceLayerName,
      paint: {
        'line-color': fillColor,
        'line-width': 0.4,
        'line-opacity': 0.7,
      },
    })

    this.legendCallback?.({
      visible: true,
      title: label,
      type: 'categorical',
      stops: [{ value: label, color: fillColor, label }],
    })
  }

  /**
   * Remove a PMTiles water layer by id (matches addWaterPMTilesLayer).
   * @param {string} layerId
   */
  removeWaterPMTilesLayer(layerId) {
    const map = this.globe?.map
    if (!map) return
    const sourceId = `${layerId}-src`
    const fillId = `${layerId}-fill`
    const lineId = `${layerId}-line`
    if (map.getLayer(fillId)) map.removeLayer(fillId)
    if (map.getLayer(lineId)) map.removeLayer(lineId)
    if (map.getSource(sourceId)) map.removeSource(sourceId)
    this.legendCallback?.({ visible: false })
  }

  /**
   * Show Köppen climate zones as a categorical fill layer.
   * Uses MapLibre match expression for data-driven fill-color per climate code.
   *
   * @param {object} geojson - FeatureCollection with properties: code, group, climate_class, name, color
   */
  showClimateZones(geojson) {
    this.clearLayers()

    if (!geojson?.features?.length) return

    console.log(`[layers] showClimateZones: ${geojson.features.length} features`)

    // Store active choropleth info for popup display
    this._activeChoropleth = { attribute: 'name', displayName: 'Climate Zone' }
    this.globe._activeChoropleth = this._activeChoropleth

    // Stamp _fill on each feature from its color property
    const legendStops = []
    const seenCodes = new Set()

    for (const f of geojson.features) {
      const code = f.properties?.code
      const color = f.properties?.color
      f.properties._fill = color || '#808080'

      if (code && color && !seenCodes.has(code)) {
        seenCodes.add(code)
        legendStops.push({ value: code, color, label: `${code} — ${f.properties.name || code}` })
      }
    }

    // Sort legend by group then code
    legendStops.sort((a, b) => a.value.localeCompare(b.value))

    // Build categorical legend grouped by climate class
    const groups = {}
    for (const stop of legendStops) {
      const group = stop.value.charAt(0)
      const groupName =
        { A: 'Tropical', B: 'Arid', C: 'Temperate', D: 'Continental', E: 'Polar' }[group] || group
      if (!groups[groupName]) groups[groupName] = []
      groups[groupName].push(stop)
    }

    const legendConfig = {
      visible: true,
      title: 'Köppen Climate Classification',
      type: 'categorical',
      stops: legendStops,
      groups,
    }

    // Route through ChoroplethStyleManager for extrusion + mode switcher support
    this.choroplethStyleManager.setPreclassifiedData({
      enrichedGeojson: geojson,
      attribute: null, // categorical — uses fixed height for extrusion
      linePaint: { 'line-color': 'rgba(255,255,255,0.3)', 'line-width': 0.5 },
      legendConfig,
    })
  }

  /**
   * Show world timezones as a diverging choropleth on UTC offset.
   * Features should have _fill stamped by src/ai/ops/timezones.js using the
   * EarthVoice brand palette: Deep Ocean Blue (-12) → White (0) → Turquoise (+14).
   * @param {object} geojson - GeoJSON FeatureCollection with _fill on each feature
   * @param {object} opts - { dstMode, single, qa } — adjusts legend title only
   */
  showTimezones(geojson, opts = {}) {
    this.clearLayers()

    if (!geojson?.features?.length) return

    const { dstMode = false } = opts

    console.log(`[layers] showTimezones: ${geojson.features.length} zones (dstMode=${dstMode})`)

    this._activeChoropleth = {
      attribute: dstMode ? 'utc_offset_dst_hours' : 'utc_offset_hours',
      displayName: dstMode ? 'UTC Offset (DST)' : 'UTC Offset',
    }
    this.globe._activeChoropleth = this._activeChoropleth

    // Build a discrete legend for the diverging UTC scale.
    // Sample at every 2 hours for readability (-12, -10, ... +14). Skip
    // offsets where no feature is present in the current view.
    const legendStops = []
    const offsetField = dstMode ? 'utc_offset_dst_hours' : 'utc_offset_hours'
    for (let h = -12; h <= 14; h += 2) {
      const sample = geojson.features.find(
        (f) => Math.round(Number(f.properties[offsetField])) === h
      )
      if (!sample?.properties._fill) continue
      const sign = h >= 0 ? '+' : '-'
      const abs = Math.abs(h)
      const label = `UTC${sign}${String(abs).padStart(2, '0')}:00`
      legendStops.push({ value: h, color: sample.properties._fill, label })
    }

    const legendConfig = {
      visible: true,
      title: dstMode ? 'World Time Zones (Daylight Saving)' : 'World Time Zones',
      type: 'gradient',
      stops: legendStops,
    }

    this.choroplethStyleManager.setPreclassifiedData({
      enrichedGeojson: geojson,
      attribute: null, // discrete offset values; height is fixed
      linePaint: { 'line-color': 'rgba(255,255,255,0.3)', 'line-width': 0.5 },
      legendConfig,
    })
  }

  /**
   * Show Freedom House political freedom index as a 3-class categorical choropleth.
   * Green = Free, Yellow = Partly Free, Red = Not Free.
   * @param {object} geojson - GeoJSON FeatureCollection with status_code and color properties
   */
  showFreedomIndex(geojson) {
    this.clearLayers()

    if (!geojson?.features?.length) return

    console.log(`[layers] showFreedomIndex: ${geojson.features.length} features`)

    // Store active choropleth info for popup display
    this._activeChoropleth = { attribute: 'status', displayName: 'Freedom Status' }
    this.globe._activeChoropleth = this._activeChoropleth

    // Color map for status codes
    const STATUS_COLORS = {
      F: '#2ecc71', // Green — Free
      PF: '#f1c40f', // Yellow — Partly Free
      NF: '#e74c3c', // Red — Not Free
    }

    // Stamp _fill on each feature from its status_code
    for (const f of geojson.features) {
      const code = f.properties?.status_code
      f.properties._fill = STATUS_COLORS[code] || '#808080'
    }

    // Build legend stops
    const allStops = [
      { value: 'F', color: '#2ecc71', label: 'Free' },
      { value: 'PF', color: '#f1c40f', label: 'Partly Free' },
      { value: 'NF', color: '#e74c3c', label: 'Not Free' },
    ]

    // Only include stops that have data in the current view
    const presentCodes = new Set(geojson.features.map((f) => f.properties?.status_code))
    const filteredStops = allStops.filter((s) => presentCodes.has(s.value))

    const legendConfig = {
      visible: true,
      title: 'Freedom House Index',
      type: 'categorical',
      stops: filteredStops,
    }

    // Route through ChoroplethStyleManager for extrusion + mode switcher support
    this.choroplethStyleManager.setPreclassifiedData({
      enrichedGeojson: geojson,
      attribute: null, // categorical — uses fixed height for extrusion
      linePaint: { 'line-color': 'rgba(255,255,255,0.5)', 'line-width': 0.8 },
      legendConfig,
    })
  }

  /**
   * Show Fragile States Index as a gradient choropleth (YlOrRd).
   * Features must have _fill property pre-computed by the operation handler.
   * @param {object} geojson - GeoJSON FeatureCollection with _fill colors
   * @param {object} opts - { attribute, attributeLabel, min, max }
   */
  showFragileStates(geojson, opts = {}) {
    this.clearLayers()

    if (!geojson?.features?.length) return

    const { attribute = 'fsi_total', attributeLabel = 'FSI Total Score', min = 0, max = 120 } = opts

    console.log(
      `[layers] showFragileStates: ${geojson.features.length} features, attribute=${attribute}, range=${min}-${max}`
    )

    this._activeChoropleth = { attribute, displayName: attributeLabel }
    this.globe._activeChoropleth = this._activeChoropleth

    // Build 7-stop gradient legend
    const YLOR_RD = [
      [255, 255, 204],
      [255, 237, 160],
      [254, 217, 118],
      [254, 178, 76],
      [253, 141, 60],
      [252, 78, 42],
      [189, 0, 38],
    ]
    const rgbHex = ([r, g, b]) =>
      '#' + [r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')
    const step = (max - min) / (YLOR_RD.length - 1)
    const legendStops = YLOR_RD.map((rgb, i) => ({
      value: Math.round(min + step * i),
      color: rgbHex(rgb),
      label:
        i === 0
          ? `${Math.round(min)} (Stable)`
          : i === YLOR_RD.length - 1
            ? `${Math.round(max)} (Alert)`
            : `${Math.round(min + step * i)}`,
    }))

    const legendConfig = {
      visible: true,
      title: attributeLabel,
      type: 'gradient',
      stops: legendStops,
    }

    // Route through ChoroplethStyleManager for extrusion + mode switcher support
    this.choroplethStyleManager.setPreclassifiedData({
      enrichedGeojson: geojson,
      attribute,
      linePaint: { 'line-color': 'rgba(255,255,255,0.5)', 'line-width': 0.8 },
      legendConfig,
    })
  }

  /**
   * Show FAOSTAT agricultural data as a gradient choropleth.
   * Features must have _fill property pre-computed by the operation handler.
   * @param {object} geojson - GeoJSON FeatureCollection with _fill colors
   * @param {object} opts - { attribute, attributeLabel, min, max, rampName }
   */
  showAgriculture(geojson, opts = {}) {
    this.clearLayers()

    if (!geojson?.features?.length) return

    const {
      attribute = 'cereal_production_mt',
      attributeLabel = 'Cereal Production (Mt)',
      min = 0,
      max = 100,
      rampName = 'YlGn',
    } = opts

    console.log(
      `[layers] showAgriculture: ${geojson.features.length} features, attribute=${attribute}, range=${min}-${max}`
    )

    this._activeChoropleth = { attribute, displayName: attributeLabel }
    this.globe._activeChoropleth = this._activeChoropleth

    // Build gradient legend from the ramp used
    const RAMPS = {
      YlGn: [
        [255, 255, 229],
        [247, 252, 185],
        [217, 240, 163],
        [173, 221, 142],
        [120, 198, 121],
        [49, 163, 84],
        [0, 104, 55],
      ],
      YlOrRd: [
        [255, 255, 204],
        [255, 237, 160],
        [254, 217, 118],
        [254, 178, 76],
        [253, 141, 60],
        [252, 78, 42],
        [189, 0, 38],
      ],
      BuGn: [
        [237, 248, 251],
        [204, 236, 230],
        [153, 216, 201],
        [102, 194, 164],
        [65, 174, 118],
        [35, 139, 69],
        [0, 88, 36],
      ],
      PuBu: [
        [241, 238, 246],
        [208, 209, 230],
        [166, 189, 219],
        [116, 169, 207],
        [54, 144, 192],
        [5, 112, 176],
        [3, 78, 123],
      ],
    }
    const colors = RAMPS[rampName] || RAMPS.YlGn
    const rgbHex = ([r, g, b]) =>
      '#' + [r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')
    const step = (max - min) / (colors.length - 1)

    const fmtVal = (v) => {
      if (v >= 1000) return Math.round(v).toLocaleString()
      if (v >= 10) return Math.round(v).toString()
      return v.toFixed(1)
    }

    const legendStops = colors.map((rgb, i) => ({
      value: min + step * i,
      color: rgbHex(rgb),
      label: i === 0 ? fmtVal(min) : i === colors.length - 1 ? fmtVal(max) : fmtVal(min + step * i),
    }))

    const legendConfig = {
      visible: true,
      title: attributeLabel,
      type: 'gradient',
      stops: legendStops,
    }

    // Route through ChoroplethStyleManager for extrusion + mode switcher support
    this.choroplethStyleManager.setPreclassifiedData({
      enrichedGeojson: geojson,
      attribute,
      linePaint: { 'line-color': 'rgba(255,255,255,0.5)', 'line-width': 0.8 },
      legendConfig,
    })
  }

  /**
   * Show POI data as H3 hexagonal density bins.
   * @param {Array} rows - Tabular rows with latitude/longitude
   * @param {object} options - { colorRamp, label, resolution }
   */
  showH3Density(rows, options = {}) {
    this.clearLayers()

    const points = rows
      .filter((r) => r.latitude != null && r.longitude != null)
      .map((r) => ({ lat: r.latitude, lng: r.longitude, name: r.name, category: r.category }))

    if (points.length === 0) return

    // Use smart resolution based on point count (zoom may be stale during fly animation)
    const zoom = this.globe.map.getZoom()
    const resolution = options.resolution || smartResolution(points.length, zoom)
    const hexData = aggregateToH3(points, resolution)
    const colorRamp = options.colorRamp || 'YlOrRd'
    const label = options.label || `Density (${points.length.toLocaleString()} points)`

    console.log(
      `[layers] H3 density: ${points.length} points → ${hexData.length} hexagons at resolution ${resolution} (zoom=${zoom.toFixed(1)})`
    )

    // Store H3 state for extrusion toggling (avoids re-querying DuckDB)
    this._h3State = { hexData, colorRamp, label, extruded: false }

    const h3Layer = createH3Layer('h3-density', hexData, {
      colorRamp,
      opacity: 0.55,
    })

    // Render via Deck.gl overlay
    this.globe.deckOverlay.setProps({ layers: [h3Layer] })
    this._deckLayerActive = true

    // Update legend
    this.legendCallback?.(buildH3LegendConfig(hexData, label, colorRamp))

    // Notify mode switcher that H3 is active (show flat/extruded buttons)
    this._onH3ModeChange?.('flat')
  }

  /**
   * Check if an H3 density layer is currently active.
   * @returns {boolean}
   */
  hasH3Density() {
    return !!(this._h3State && this._h3State.hexData?.length > 0)
  }

  /**
   * Toggle H3 density layer between flat and extruded modes.
   * Re-renders from cached hex data — no re-query needed.
   * @param {'flat'|'extruded'} mode
   */
  setH3Mode(mode) {
    if (!this._h3State) return

    const { hexData, colorRamp, label, category } = this._h3State
    const extruded = mode === 'extruded'
    this._h3State.extruded = extruded

    if (extruded) {
      // 3D mode: native MapLibre fill-extrusion, which follows the globe
      // projection. Deck.gl's extrusion is kept only as a fallback — it
      // renders on its own flat viewport, so its columns detach from the
      // sphere the moment the camera tilts, which is exactly what the
      // 3D button used to do.
      if (this._renderH3Native(hexData, colorRamp, category, { extruded: true })) {
        this._deckLayerActive = false
        try {
          this.globe.deckOverlay?.setProps?.({ layers: [] })
        } catch {
          /* overlay may not be mounted */
        }
      } else {
        const h3Layer = createH3Layer('h3-preagg-density', hexData, {
          colorRamp,
          opacity: 0.8,
          extruded: true,
          elevationScale: 5000,
        })
        this.globe.deckOverlay.setProps({ layers: [h3Layer] })
        this._deckLayerActive = true
      }
      this.globe.map.easeTo({ pitch: 55, duration: 800 })
    } else {
      // Flat mode: re-render as flat Deck.gl H3HexagonLayer
      this.showH3PreAggregated(hexData, { colorRamp, label, category })
      this.globe.map.easeTo({ pitch: 0, duration: 800 })
    }

    console.log(`[layers] H3 mode: ${mode} (${hexData.length} hexagons)`)
  }

  /**
   * Show pre-aggregated H3 hex data from the enriched Parquet pipeline.
   * Unlike showH3Density (which aggregates raw POI points client-side),
   * this takes hex data that already has { hexId, count, population, perCapita }.
   *
   * @param {Array} hexData - Array of { hexId: string, count: number, population?, perCapita? }
   * @param {object} options - { colorRamp, label, category }
   */
  showH3PreAggregated(hexData, options = {}) {
    this.clearLayers()

    if (!hexData || hexData.length === 0) return

    const colorRamp = options.colorRamp || 'YlOrRd'
    const category = options.category || 'POI'
    const label = options.label || `Density (${hexData.length.toLocaleString()} hexagons)`

    console.warn(`[layers] H3 pre-aggregated: ${hexData.length} hexagons, colorRamp=${colorRamp}`)

    // Store state for extrusion toggling
    this._h3State = { hexData, colorRamp, label, category, extruded: false }

    // ── Render with MapLibre's own renderer ──────────────────────
    // Deck.gl projects with its own Web-Mercator viewport, which does not
    // follow MapLibre v5's globe projection: the hexagons sat on a flat plane
    // while the basemap was a sphere and visibly detached from the globe as
    // soon as the camera was tilted. Native fill layers are projection-aware,
    // so they stay on the surface at any pitch. Deck.gl remains the fallback.
    if (!this._renderH3Native(hexData, colorRamp, category)) {
      const h3Layer = createH3Layer('h3-preagg-density', hexData, {
        colorRamp,
        opacity: 0.55,
      })
      this.globe.deckOverlay.setProps({ layers: [h3Layer] })
      this._deckLayerActive = true
    }

    // Update legend
    this.legendCallback?.(buildH3LegendConfig(hexData, label, colorRamp))

    // Show mode switcher (flat/extruded toggle)
    this._onH3ModeChange?.('flat')
  }

  /**
   * Render pre-aggregated H3 hexagons as native MapLibre polygons.
   * Returns true on success, false to signal the caller to fall back to Deck.gl.
   */
  _renderH3Native(hexData, colorRamp, category, opts = {}) {
    const map = this.globe?.map
    if (!map || typeof map.addSource !== 'function') return false

    const extruded = !!opts.extruded
    const srcId = 'data-h3-density-src'
    const fillId = 'data-h3-density-fill'
    const lineId = 'data-h3-density-line'
    const extId = 'data-h3-density-3d'

    try {
      const geojson = buildH3GeoJSON(hexData, colorRamp)
      if (!geojson.features.length) return false

      // Idempotent: drop any previous version before re-adding.
      for (const id of [lineId, fillId, extId]) {
        if (map.getLayer(id)) map.removeLayer(id)
      }
      if (map.getSource(srcId)) map.removeSource(srcId)

      map.addSource(srcId, { type: 'geojson', data: geojson })

      if (extruded) {
        // Native fill-extrusion is projection-aware, so the 3D columns stay
        // rooted on the sphere as the camera tilts. Deck.gl's extrusion built
        // its geometry on a flat Web-Mercator plane and slid off the globe.
        map.addLayer({
          id: extId,
          type: 'fill-extrusion',
          source: srcId,
          paint: {
            'fill-extrusion-color': ['get', '_fill'],
            'fill-extrusion-height': ['get', '_height'],
            'fill-extrusion-base': 0,
            'fill-extrusion-opacity': 0.9,
          },
        })
        return true
      }

      map.addLayer({
        id: fillId,
        type: 'fill',
        source: srcId,
        paint: {
          'fill-color': ['get', '_fill'],
          'fill-opacity': ['get', '_alpha'],
        },
      })
      map.addLayer({
        id: lineId,
        type: 'line',
        source: srcId,
        paint: {
          'line-color': 'rgba(255, 210, 0, 0.30)',
          'line-width': 0.5,
        },
      })

      this._setupH3Popup(category)
      return true
    } catch (err) {
      console.warn('[layers] native H3 render failed, falling back to Deck.gl:', err.message)
      return false
    }
  }

  /**
   * Set up a MapLibre click popup for H3 hexagon inspection.
   * Shows the same data as the Deck.gl tooltip but via native MapLibre popup.
   */
  _setupH3Popup(category) {
    const map = this.globe.map
    const fillLayerId = 'data-h3-density-fill'

    // Change cursor on hover
    map.on('mouseenter', fillLayerId, () => {
      map.getCanvas().style.cursor = 'pointer'
    })
    map.on('mouseleave', fillLayerId, () => {
      map.getCanvas().style.cursor = ''
    })

    // Click popup
    this._h3ClickHandler = (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [fillLayerId] })
      if (!features.length) return

      const props = features[0].properties
      const cat = category.replace(/_/g, ' ')
      const capCat = cat.charAt(0).toUpperCase() + cat.slice(1)
      const rawCount = props.rawCount || props.count
      const pop = props.population ? Number(props.population).toLocaleString() : 'N/A'
      const perCap = props.perCapita != null ? Number(props.perCapita).toFixed(1) : 'N/A'

      new maplibregl.Popup({ className: 'ev-popup', maxWidth: '240px' })
        .setLngLat(e.lngLat)
        .setHTML(
          `
          <div style="font-family: 'IBM Plex Sans', sans-serif; font-size: 13px; line-height: 1.6;">
            <div style="font-weight: 600; color: #F5A623;">${capCat}s: ${Number(rawCount).toLocaleString()}</div>
            <div>Population: ${pop}</div>
            <div style="font-weight: 600; color: #8fd4d4;">Per 10K people: ${perCap}</div>
          </div>
        `
        )
        .addTo(map)
    }
    map.on('click', fillLayerId, this._h3ClickHandler)
  }

  // ── Line visualizations (MapLibre native) ─────────────────

  /**
   * Show rivers/waterways as styled line features.
   * @param {object} geojson - GeoJSON FeatureCollection with LineString/MultiLineString
   * @param {object} options - { minScalerank, showLabels, clearExisting }
   */
  showRivers(geojson, options = {}) {
    // Rivers are additive — don't clear existing layers by default
    if (options.clearExisting !== false) {
      // Only clear river layers, not boundaries or markers
      this.globe.clearNativeLayers('data-rivers')
    }

    const minRank = options.minScalerank ?? 0
    const filtered = {
      type: 'FeatureCollection',
      features: geojson.features.filter((f) => {
        const rank = f.properties?.scalerank ?? 99
        const cls = f.properties?.featurecla || ''
        // Filter by scale rank and exclude lake centerlines unless requested
        return rank <= minRank || minRank === 0 ? cls === 'River' || options.includeLakes : false
      }),
    }

    // If no minRank filter, show all rivers (exclude lake centerlines by default)
    if (minRank === 0) {
      filtered.features = geojson.features.filter(
        (f) => f.properties?.featurecla === 'River' || options.includeLakes
      )
    }

    console.log(
      `[LayerManager] showRivers: ${filtered.features.length} rivers (from ${geojson.features.length} total)`
    )

    // Line width varies by river importance (scalerank)
    this.globe.addLineLayer('data-rivers', filtered, {
      line: {
        'line-color': '#4fc3f7',
        'line-width': [
          'interpolate',
          ['linear'],
          ['get', 'scalerank'],
          1,
          3.0, // Major rivers (Amazon, Nile, Mississippi)
          2,
          2.5,
          3,
          2.0,
          4,
          1.5,
          5,
          1.2,
          6,
          0.8, // Minor rivers
        ],
        'line-opacity': 0.85,
      },
      label: options.showLabels !== false,
      labelField: 'name_en',
      labelColor: '#b3e5fc',
    })

    // River legend
    this.legendCallback?.({
      visible: true,
      title: 'Rivers',
      type: 'categorical',
      stops: [
        { color: [79, 195, 247, 255], label: 'Major river' },
        { color: [79, 195, 247, 180], label: 'Minor river' },
      ],
    })
  }

  /**
   * Show a physical feature layer (mountains, deserts, glaciers, reefs, lakes).
   * Each feature type gets a thematic color and appropriate geometry rendering.
   *
   * @param {string} featureType - 'mountains'|'deserts'|'glaciated'|'reefs'|'lakes'
   * @param {object} geojson - FeatureCollection
   * @param {object} [options] - { clearExisting, showLabels }
   */
  showPhysicalFeature(featureType, geojson, options = {}) {
    if (options.clearExisting !== false) {
      this.globe.clearNativeLayers(`data-${featureType}`)
    }

    if (!geojson?.features?.length) {
      console.warn(`[LayerManager] showPhysicalFeature: no features for ${featureType}`)
      return
    }

    const STYLES = {
      mountains: {
        type: 'point',
        color: '#795548', // Brown
        labelColor: '#BCAAA4',
        legendTitle: 'Mountain Ranges',
        legendColor: [121, 85, 72, 255],
        legendLabel: 'Mountain / range',
        circleRadius: ['interpolate', ['linear'], ['zoom'], 1, 2, 5, 5, 10, 8],
        circleOpacity: 0.85,
      },
      deserts: {
        type: 'polygon',
        fillColor: '#D2B48C', // Sandy tan
        fillOpacity: 0.35,
        lineColor: '#C4A882',
        lineWidth: 1,
        labelColor: '#DEC8A0',
        legendTitle: 'Deserts',
        legendColor: [210, 180, 140, 200],
        legendLabel: 'Desert / arid region',
      },
      glaciated: {
        type: 'polygon',
        fillColor: '#E0F7FA', // Ice white-blue
        fillOpacity: 0.45,
        lineColor: '#B2EBF2',
        lineWidth: 0.8,
        labelColor: '#E0F7FA',
        legendTitle: 'Glaciers & Ice Sheets',
        legendColor: [224, 247, 250, 220],
        legendLabel: 'Glaciated area',
      },
      reefs: {
        type: 'line',
        lineColor: '#00BCD4', // Cyan
        lineWidth: 2,
        lineOpacity: 0.9,
        labelColor: '#80DEEA',
        legendTitle: 'Coral Reefs',
        legendColor: [0, 188, 212, 230],
        legendLabel: 'Coral reef',
      },
      lakes: {
        type: 'polygon',
        fillColor: '#1565C0', // Deep blue
        fillOpacity: 0.5,
        lineColor: '#42A5F5',
        lineWidth: 1,
        labelColor: '#90CAF9',
        legendTitle: 'Lakes',
        legendColor: [21, 101, 192, 200],
        legendLabel: 'Lake',
      },
      oceans: {
        type: 'polygon',
        fillColor: '#0288D1', // Ocean blue
        // Oceans cover huge areas, so keep the fill light enough that the
        // continents and basemap still read through it.
        fillOpacity: 0.3,
        lineColor: '#4FC3F7',
        lineWidth: 1,
        labelColor: '#B3E5FC',
        legendTitle: 'Oceans',
        legendColor: [2, 136, 209, 160],
        legendLabel: 'Ocean',
      },
      seas: {
        type: 'polygon',
        fillColor: '#00ACC1', // Cyan — distinct from the deeper ocean blue
        fillOpacity: 0.45,
        lineColor: '#4DD0E1',
        lineWidth: 1,
        labelColor: '#B2EBF2',
        legendTitle: 'Seas',
        legendColor: [0, 172, 193, 180],
        legendLabel: 'Sea',
      },
      gulfs: {
        type: 'polygon',
        fillColor: '#00838F', // Dark teal
        fillOpacity: 0.5,
        lineColor: '#26C6DA',
        lineWidth: 1,
        labelColor: '#B2EBF2',
        legendTitle: 'Gulfs',
        legendColor: [0, 131, 143, 190],
        legendLabel: 'Gulf',
      },
      bays: {
        type: 'polygon',
        fillColor: '#26A69A', // Teal-green
        fillOpacity: 0.5,
        lineColor: '#80CBC4',
        lineWidth: 1,
        labelColor: '#B2DFDB',
        legendTitle: 'Bays',
        legendColor: [38, 166, 154, 190],
        legendLabel: 'Bay',
      },
      geographic_lines: {
        type: 'line',
        lineColor: '#FFD54F', // Bright gold — stands out on the satellite globe
        lineWidth: 2,
        lineOpacity: 0.9,
        labelColor: '#FFF59D',
        legendTitle: 'Reference Lines',
        legendColor: [255, 213, 79, 230],
        legendLabel: 'Latitude / reference line',
      },
      // CL-170 (roadmap H3.1): numbered lat/long grid generated client-side
      // by ops/graticule.js. Cool blue so it reads as scaffolding, distinct
      // from the gold famous reference lines when both are shown.
      graticule: {
        type: 'line',
        lineColor: '#4FC3F7',
        lineWidth: 1.2,
        lineOpacity: 0.7,
        labelColor: '#B3E5FC',
        legendTitle: 'Coordinate Grid',
        legendColor: [79, 195, 247, 200],
        legendLabel: 'Latitude / longitude every 15°',
      },
      biomes: {
        type: 'polygon',
        // Per-feature colour: each biome paints in its official Dinerstein
        // (RESOLVE Ecoregions 2017) colour via ['get','color'].
        colorField: 'color',
        fillColor: '#4CAF50', // fallback only
        fillOpacity: 0.55,
        lineColor: 'rgba(0,0,0,0.2)',
        lineWidth: 0.3,
        // Biome names are long ("Tropical & Subtropical Moist Broadleaf
        // Forests"); skip on-map labels to avoid clutter — the legend, click
        // popup, and lesson narration name them instead.
        noLabels: true,
        labelColor: '#ffffff',
        legendTitle: 'Biomes',
        legendColor: [76, 175, 80, 160],
        legendLabel: 'Biome',
        // Full color key — swatches match the per-feature Dinerstein fills.
        // Labels are shortened from the official RESOLVE biome names so the
        // panel stays readable; grouped forests → grasslands → other.
        legendItems: [
          { color: '#38A700', label: 'Tropical Moist Forest' },
          { color: '#CCCD65', label: 'Tropical Dry Forest' },
          { color: '#88CE66', label: 'Tropical Conifer Forest' },
          { color: '#00734C', label: 'Temperate Broadleaf Forest' },
          { color: '#458970', label: 'Temperate Conifer Forest' },
          { color: '#7AB6F5', label: 'Boreal Forest / Taiga' },
          { color: '#FEAA01', label: 'Tropical Grassland & Savanna' },
          { color: '#FEFF73', label: 'Temperate Grassland' },
          { color: '#BEE7FF', label: 'Flooded Grassland' },
          { color: '#D6C39D', label: 'Montane Grassland' },
          { color: '#9ED7C2', label: 'Tundra' },
          { color: '#FE0000', label: 'Mediterranean Woodland' },
          { color: '#CC6767', label: 'Desert & Xeric Shrub' },
          { color: '#FE01C4', label: 'Mangroves' },
          { color: '#FFEAAF', label: 'Rock & Ice' },
        ],
        // CC-BY 4.0 — shown in the map attribution control while biomes display.
        attribution: 'Ecoregions 2017 &copy; RESOLVE (CC-BY 4.0)',
      },
      climate_zones: {
        type: 'polygon',
        colorField: 'color', // per-zone: polar blue, temperate green, tropical orange
        fillColor: '#90A4AE', // fallback only
        // Stronger fills than other layers: the band COLOR is the lesson here
        // (hot=orange, mild=green, cold=blue), so it must read true over the
        // dark satellite ocean rather than muddying toward brown at low opacity.
        fillOpacity: 0.62,
        lineColor: 'rgba(255,255,255,0.4)',
        lineWidth: 0.4,
        labelColor: '#ffffff',
        legendTitle: 'Climate Zones',
        legendColor: [129, 199, 132, 150],
        legendLabel: 'Climate zone',
        // 3-key legend — swatch colors match the band fills (hot/mild/cold).
        legendItems: [
          { color: '#FF8A65', label: 'Hot (Tropical)' },
          { color: '#81C784', label: 'Mild (Temperate)' },
          { color: '#4FC3F7', label: 'Cold (Polar)' },
        ],
      },
      landforms: {
        type: 'polygon',
        // Per-feature colour: each landform paints by its landform_type via
        // ['get','color'] — same pattern as biomes.
        colorField: 'color',
        fillColor: '#C5E1A5', // fallback only
        fillOpacity: 0.55,
        lineColor: 'rgba(0,0,0,0.25)',
        lineWidth: 0.3,
        // Names vary in length; keep on-map labels for the big named features.
        labelColor: '#ffffff',
        legendTitle: 'Landforms',
        legendColor: [197, 225, 165, 160],
        legendLabel: 'Landform',
        // 6-key legend — swatches match the per-feature landform_type fills.
        legendItems: [
          { color: '#C5E1A5', label: 'Plain' },
          { color: '#D7CCC8', label: 'Plateau' },
          { color: '#FFCC80', label: 'Peninsula' },
          { color: '#B0BEC5', label: 'Basin' },
          { color: '#A5D6A7', label: 'Valley' },
          { color: '#80DEEA', label: 'Island' },
        ],
        attribution: 'Natural Earth (public domain)',
      },
      capitals: {
        type: 'point',
        color: '#FFD54F', // gold star-dot for national capitals
        labelColor: '#FFF3C4',
        legendTitle: 'Capital Cities',
        legendColor: [255, 213, 79, 255],
        legendLabel: 'Capital city',
        circleRadius: ['interpolate', ['linear'], ['zoom'], 1, 3, 4, 5, 8, 8],
        circleOpacity: 0.95,
        attribution: 'Natural Earth (public domain)',
      },
      major_cities: {
        type: 'point',
        color: '#FF7043', // orange dot, sized by population
        labelColor: '#FFCCBC',
        legendTitle: 'Major Cities',
        legendColor: [255, 112, 67, 230],
        legendLabel: 'City (dot size = population)',
        // Radius scales with population so "big and small" reads visually.
        circleRadius: [
          'interpolate',
          ['linear'],
          ['coalesce', ['get', 'population'], 0],
          800000,
          3,
          5000000,
          7,
          20000000,
          14,
        ],
        circleOpacity: 0.85,
        attribution: 'Natural Earth (public domain)',
      },
      animals: {
        type: 'point',
        emojiField: 'emoji', // render the animal emoji as the marker
        color: '#FFB300', // unused (emoji marker) but kept for legend swatch
        labelColor: '#FFE082',
        legendTitle: 'Animals & Their Homes',
        legendColor: [255, 179, 0, 230],
        legendLabel: 'Animal',
      },
    }

    const style = STYLES[featureType]
    if (!style) {
      console.warn(`[LayerManager] Unknown physical feature type: ${featureType}`)
      return
    }

    const layerId = `data-${featureType}`
    const showLabels = !style.noLabels && options.showLabels !== false
    const labelField =
      'name_en' in (geojson.features[0]?.properties || {})
        ? 'name_en'
        : 'name' in (geojson.features[0]?.properties || {})
          ? 'name'
          : 'NAME'

    console.log(
      `[LayerManager] showPhysicalFeature: ${featureType} — ${geojson.features.length} features`
    )

    if (style.type === 'polygon') {
      this.globe.addPolygonLayer(layerId, geojson, {
        fill: {
          // Data-driven colour when the style declares a colorField (biomes);
          // otherwise the single style fill colour.
          'fill-color': style.colorField ? ['get', style.colorField] : style.fillColor,
          'fill-opacity': style.fillOpacity,
        },
        line: {
          'line-color': style.lineColor,
          'line-width': style.lineWidth,
          'line-opacity': 0.8,
        },
        attribution: style.attribution,
        label: showLabels,
        labelField,
        labelColor: style.labelColor,
      })
      // Polar Plateau is sliced into 12 longitude parts (to avoid the
      // globe-spanning fill swirl), which makes its on-map label repeat once
      // per slice around the South Pole. Drop that one label — it's Antarctica's
      // interior and not Grade 1 vocabulary; the fill + legend still convey it.
      const labelLayer = `${layerId}-label`
      if (featureType === 'landforms' && this.globe.map?.getLayer(labelLayer)) {
        this.globe.map.setFilter(labelLayer, [
          '!=',
          ['coalesce', ['get', 'name_en'], ['get', 'name'], ''],
          'Polar Plateau',
        ])
      }
    } else if (style.type === 'line') {
      this.globe.addLineLayer(layerId, geojson, {
        line: {
          'line-color': style.lineColor,
          'line-width': style.lineWidth,
          'line-opacity': style.lineOpacity,
        },
        label: showLabels,
        labelField,
        labelColor: style.labelColor,
      })
    } else if (style.type === 'point') {
      // For point features (mountains), use a circle layer via addPolygonLayer hack
      // or add directly to MapLibre
      const map = this.globe.map
      if (map) {
        const sourceId = `${layerId}-src`
        if (map.getSource(sourceId)) {
          map.getSource(sourceId).setData(geojson)
        } else {
          map.addSource(sourceId, { type: 'geojson', data: geojson })
        }

        // Emoji-marker layers (e.g. animals) render the emoji glyph itself as
        // the marker instead of a circle. Otherwise draw the standard dot.
        if (style.emojiField) {
          const emojiLayerId = `${layerId}-emoji`
          if (!map.getLayer(emojiLayerId)) {
            map.addLayer({
              id: emojiLayerId,
              type: 'symbol',
              source: sourceId,
              layout: {
                'text-field': ['get', style.emojiField],
                'text-size': ['interpolate', ['linear'], ['zoom'], 0.5, 22, 2, 28, 5, 38],
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              },
            })
            this.globe._nativeLayerIds?.add(emojiLayerId)
          }
        } else if (!map.getLayer(layerId)) {
          map.addLayer({
            id: layerId,
            type: 'circle',
            source: sourceId,
            paint: {
              'circle-color': style.color,
              'circle-radius': style.circleRadius,
              'circle-opacity': style.circleOpacity,
              'circle-stroke-color': '#FFFFFF',
              'circle-stroke-width': 0.5,
            },
          })
          this.globe._nativeLayerIds?.add(layerId)
        }

        // Labels (name below the marker)
        if (showLabels) {
          const labelLayerId = `${layerId}-label`
          if (!map.getLayer(labelLayerId)) {
            map.addLayer({
              id: labelLayerId,
              type: 'symbol',
              source: sourceId,
              layout: {
                'text-field': ['coalesce', ['get', 'name_en'], ['get', 'name']],
                'text-size': 11,
                'text-offset': [0, style.emojiField ? 1.6 : 1.2],
                'text-anchor': 'top',
                'text-optional': true,
                'text-allow-overlap': false,
              },
              paint: {
                'text-color': style.labelColor,
                'text-halo-color': 'rgba(0,0,0,0.7)',
                'text-halo-width': 1,
              },
            })
            this.globe._nativeLayerIds?.add(labelLayerId)
          }
        }
      }
    }

    // Legend — multi-key (legendItems) when a layer defines distinct
    // categories (e.g. climate zones: hot / mild / cold), else a single swatch.
    this.legendCallback?.({
      visible: true,
      title: style.legendTitle,
      type: 'categorical',
      stops: style.legendItems
        ? style.legendItems.map((it) => ({ color: it.color, label: it.label }))
        : [{ color: style.legendColor, label: style.legendLabel }],
      // Author-curated key lists (e.g. biomes' 15 categories) show in full;
      // AI-derived categoricals keep the default cap.
      maxItems: style.legendItems ? style.legendItems.length : undefined,
    })
  }

  /**
   * Show division query results as orange scatter points.
   */
  showDivisionPoints(rows) {
    this.clearLayers()

    const layer = new ScatterplotLayer({
      id: 'division-points-layer',
      data: rows,
      getPosition: (d) => [d.longitude, d.latitude],
      getRadius: 6000,
      radiusMinPixels: 2,
      radiusMaxPixels: 8,
      radiusUnits: 'meters',
      getFillColor: [255, 170, 50, 220],
      getLineColor: [255, 255, 255, 200],
      lineWidthMinPixels: 1,
      stroked: true,
      filled: true,
      pickable: true,
      autoHighlight: true,
      highlightColor: [255, 255, 255, 100],
    })

    this._deckLayers.set('division-points-layer', layer)
    this._updateDeckOverlay()
  }

  /**
   * Show UNESCO World Heritage Sites as native MapLibre circle points on the globe.
   * Gold = Cultural, Green = Natural, Teal = Mixed, Red = Endangered.
   *
   * Uses MapLibre native circle layers (not Deck.gl) because Deck.gl layers
   * float above the globe surface when tilted — they render in Mercator space
   * while the map uses globe projection. Native MapLibre layers always stick
   * to the globe surface at any tilt angle.
   *
   * @param {Array} data - Array of { position, name, country, category, endangered, color, ... }
   * @param {Object} opts - { showEndangered, categoryFilter }
   */
  showHeritageSites(data, opts = {}) {
    this.clearLayers()

    const map = this.globe.map
    const layerId = 'data-heritage'
    const sourceId = `${layerId}-src`

    // Convert data array to GeoJSON FeatureCollection
    const geojson = {
      type: 'FeatureCollection',
      features: data.map((d) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: d.position },
        properties: {
          name: d.name,
          country: d.country,
          category: d.category,
          year_inscribed: d.year_inscribed,
          endangered: d.endangered,
          criteria: d.criteria,
          description: d.description,
          _color: d.endangered ? 'endangered' : d.category.toLowerCase(),
        },
      })),
    }

    // Category → color mapping for MapLibre data-driven styling
    const colorExpr = [
      'match',
      ['get', '_color'],
      'cultural',
      'rgba(255,215,0,0.9)',
      'natural',
      'rgba(0,200,83,0.9)',
      'mixed',
      'rgba(0,191,165,0.9)',
      'endangered',
      'rgba(255,23,68,0.9)',
      'rgba(255,215,0,0.9)', // fallback
    ]

    const glowColorExpr = [
      'match',
      ['get', '_color'],
      'cultural',
      'rgba(255,215,0,0.25)',
      'natural',
      'rgba(0,200,83,0.25)',
      'mixed',
      'rgba(0,191,165,0.25)',
      'endangered',
      'rgba(255,23,68,0.25)',
      'rgba(255,215,0,0.25)',
    ]

    // Remove any existing heritage layers
    this.globe._removeNativeSync(layerId)

    map.addSource(sourceId, { type: 'geojson', data: geojson })

    // Glow halo layer — larger, semi-transparent
    map.addLayer({
      id: `${layerId}-fill`,
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 1, 5, 4, 8, 8, 14, 12, 20],
        'circle-color': glowColorExpr,
        'circle-blur': 0.6,
        'circle-opacity': 0.8,
      },
    })

    // Main circle layer — solid with white stroke
    map.addLayer({
      id: `${layerId}-circle`,
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 1, 3, 4, 5, 8, 8, 12, 12],
        'circle-color': colorExpr,
        'circle-stroke-color': 'rgba(255,255,255,0.8)',
        'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 1, 0.5, 6, 1.5],
        'circle-opacity': 0.95,
      },
    })

    this.globe._nativeLayerIds.add(layerId)

    // Click handler for popup
    this._heritageClickHandler = (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [`${layerId}-circle`] })
      if (features.length > 0 && this.globe._popup) {
        const p = features[0].properties
        const endangeredTag = p.endangered
          ? '<span style="color:#ff1744;font-weight:bold"> — ENDANGERED</span>'
          : ''
        const rows = [
          `<div class="ev-popup-name">${p.name}${endangeredTag}</div>`,
          `<div class="ev-popup-row"><span class="ev-popup-label">Country</span><span class="ev-popup-value">${p.country}</span></div>`,
          `<div class="ev-popup-row"><span class="ev-popup-label">Category</span><span class="ev-popup-value">${p.category}</span></div>`,
          `<div class="ev-popup-row"><span class="ev-popup-label">Inscribed</span><span class="ev-popup-value">${p.year_inscribed}</span></div>`,
          `<div class="ev-popup-row"><span class="ev-popup-label">Criteria</span><span class="ev-popup-value">(${p.criteria})</span></div>`,
          `<div style="margin-top:4px;font-size:0.9em;color:#ccc">${p.description}</div>`,
        ]
        this.globe._popup.setLngLat(e.lngLat).setHTML(rows.join('')).addTo(map)
      }
    }
    map.on('click', `${layerId}-circle`, this._heritageClickHandler)

    // Cursor change on hover
    map.on('mouseenter', `${layerId}-circle`, () => {
      map.getCanvas().style.cursor = 'pointer'
    })
    map.on('mouseleave', `${layerId}-circle`, () => {
      map.getCanvas().style.cursor = ''
    })

    // Build categorical legend
    const legendStops = [
      { color: 'rgba(255,215,0,0.9)', label: 'Cultural' },
      { color: 'rgba(0,200,83,0.9)', label: 'Natural' },
      { color: 'rgba(0,191,165,0.9)', label: 'Mixed' },
    ]

    // Add endangered entry if any are present
    const hasEndangered = data.some((d) => d.endangered)
    if (hasEndangered) {
      legendStops.push({ color: 'rgba(255,23,68,0.9)', label: 'Endangered' })
    }

    this.legendCallback?.({
      visible: true,
      title: 'UNESCO WORLD HERITAGE SITES',
      type: 'categorical',
      stops: legendStops,
    })
  }

  /**
   * Show volcanoes as native MapLibre circle points on the globe.
   * Colored by activity status:
   *   Red (#F44336)    = Historical (erupted since 1900)
   *   Orange (#FF9800) = Holocene (erupted in last 12,000 years)
   *   Gray (#9E9E9E)   = Pleistocene (older)
   * Circle radius scaled by VEI (Volcanic Explosivity Index) when available.
   *
   * @param {Array} features - GeoJSON Feature array with Point geometries + volcano properties
   * @param {Object} opts - { activeOnly, typeFilter }
   */
  // ACLED conflict events — circles colored by event_type, sized by fatalities.
  showAcled(features, _opts = {}) {
    this.clearLayers()
    const map = this.globe.map
    const layerId = 'data-acled'
    const sourceId = `${layerId}-src`

    const geojson = {
      type: 'FeatureCollection',
      features: features.map((f) => ({
        type: 'Feature',
        geometry: f.geometry,
        properties: {
          ...f.properties,
          _etype: f.properties.event_type || 'Other',
          _fat: Number(f.properties.fatalities) || 0,
        },
      })),
    }

    const colorExpr = [
      'match',
      ['get', '_etype'],
      'Battles',
      'rgba(183,28,28,0.9)',
      'Explosions/Remote violence',
      'rgba(244,67,54,0.9)',
      'Violence against civilians',
      'rgba(216,27,96,0.9)',
      'Protests',
      'rgba(30,136,229,0.9)',
      'Riots',
      'rgba(142,36,170,0.9)',
      'Strategic developments',
      'rgba(120,120,120,0.8)',
      'rgba(255,152,0,0.9)', // fallback
    ]
    // Radius grows with fatalities (sqrt-ish via stepped interpolation), with a
    // floor so zero-fatality events (protests) are still visible.
    const radiusExpr = [
      'interpolate',
      ['linear'],
      ['zoom'],
      1,
      ['interpolate', ['linear'], ['get', '_fat'], 0, 2.5, 10, 4, 100, 7],
      5,
      ['interpolate', ['linear'], ['get', '_fat'], 0, 4, 10, 7, 100, 12],
      9,
      ['interpolate', ['linear'], ['get', '_fat'], 0, 6, 10, 11, 100, 20],
    ]

    if (map.getLayer(layerId)) map.removeLayer(layerId)
    if (map.getSource(sourceId)) map.removeSource(sourceId)
    map.addSource(sourceId, { type: 'geojson', data: geojson })
    map.addLayer({
      id: layerId,
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-color': colorExpr,
        'circle-radius': radiusExpr,
        'circle-stroke-width': 0.5,
        'circle-stroke-color': 'rgba(0,0,0,0.4)',
        'circle-opacity': 0.85,
      },
    })
    this._activeDataLayers = this._activeDataLayers || []
    this._activeDataLayers.push(layerId)
  }

  showVolcanoes(features, opts = {}) {
    this.clearLayers()

    const map = this.globe.map
    const layerId = 'data-volcanoes'
    const sourceId = `${layerId}-src`

    // Build GeoJSON with _activity color key
    const geojson = {
      type: 'FeatureCollection',
      features: features.map((f) => ({
        type: 'Feature',
        geometry: f.geometry,
        properties: {
          ...f.properties,
          _activity: f.properties.activity || 'holocene',
        },
      })),
    }

    // Activity → color mapping
    const colorExpr = [
      'match',
      ['get', '_activity'],
      'historical',
      'rgba(244,67,54,0.9)', // Red
      'holocene',
      'rgba(255,152,0,0.9)', // Orange
      'pleistocene',
      'rgba(158,158,158,0.7)', // Gray
      'rgba(255,152,0,0.9)', // fallback
    ]

    const glowColorExpr = [
      'match',
      ['get', '_activity'],
      'historical',
      'rgba(244,67,54,0.3)',
      'holocene',
      'rgba(255,152,0,0.25)',
      'pleistocene',
      'rgba(158,158,158,0.15)',
      'rgba(255,152,0,0.25)',
    ]

    // Activity-based radius: historical (active) volcanoes slightly larger
    const radiusExpr = [
      'interpolate',
      ['linear'],
      ['zoom'],
      1,
      ['match', ['get', '_activity'], 'historical', 3.5, 2.5],
      4,
      ['match', ['get', '_activity'], 'historical', 5.5, 4],
      8,
      ['match', ['get', '_activity'], 'historical', 8, 6],
      12,
      ['match', ['get', '_activity'], 'historical', 12, 9],
    ]

    const glowRadiusExpr = [
      'interpolate',
      ['linear'],
      ['zoom'],
      1,
      ['match', ['get', '_activity'], 'historical', 6, 4],
      4,
      ['match', ['get', '_activity'], 'historical', 9, 7],
      8,
      ['match', ['get', '_activity'], 'historical', 14, 10],
    ]

    // Remove existing volcano layers
    this.globe._removeNativeSync(layerId)

    map.addSource(sourceId, { type: 'geojson', data: geojson })

    // Glow halo layer
    map.addLayer({
      id: `${layerId}-fill`,
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-radius': glowRadiusExpr,
        'circle-color': glowColorExpr,
        'circle-blur': 0.6,
        'circle-opacity': 0.7,
      },
    })

    // Main circle layer
    map.addLayer({
      id: `${layerId}-circle`,
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-radius': radiusExpr,
        'circle-color': colorExpr,
        'circle-stroke-color': 'rgba(255,255,255,0.8)',
        'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 1, 0.5, 6, 1.5],
        'circle-opacity': 0.92,
      },
    })

    this.globe._nativeLayerIds.add(layerId)

    // Click handler for popup
    this._volcanoClickHandler = (e) => {
      const hits = map.queryRenderedFeatures(e.point, { layers: [`${layerId}-circle`] })
      if (hits.length > 0 && this.globe._popup) {
        const p = hits[0].properties
        const activityTag =
          p._activity === 'historical'
            ? '<span style="color:#F44336;font-weight:bold"> — ACTIVE</span>'
            : ''
        const rows = [
          `<div class="ev-popup-name">${p.name}${activityTag}</div>`,
          `<div class="ev-popup-row"><span class="ev-popup-label">Country</span><span class="ev-popup-value">${p.country}</span></div>`,
          `<div class="ev-popup-row"><span class="ev-popup-label">Type</span><span class="ev-popup-value">${p.type || '—'}</span></div>`,
          p.elevation_m
            ? `<div class="ev-popup-row"><span class="ev-popup-label">Elevation</span><span class="ev-popup-value">${Number(p.elevation_m).toLocaleString()}m</span></div>`
            : '',
          p.last_eruption
            ? `<div class="ev-popup-row"><span class="ev-popup-label">Last Eruption</span><span class="ev-popup-value">${p.last_eruption}</span></div>`
            : '',
          p.tectonic_setting
            ? `<div class="ev-popup-row"><span class="ev-popup-label">Tectonic</span><span class="ev-popup-value">${p.tectonic_setting}</span></div>`
            : '',
          p.pop_100km
            ? `<div class="ev-popup-row"><span class="ev-popup-label">Pop. within 100km</span><span class="ev-popup-value">${Number(p.pop_100km).toLocaleString()}</span></div>`
            : '',
        ].filter(Boolean)
        this.globe._popup.setLngLat(e.lngLat).setHTML(rows.join('')).addTo(map)
      }
    }
    map.on('click', `${layerId}-circle`, this._volcanoClickHandler)
    map.on('mouseenter', `${layerId}-circle`, () => {
      map.getCanvas().style.cursor = 'pointer'
    })
    map.on('mouseleave', `${layerId}-circle`, () => {
      map.getCanvas().style.cursor = ''
    })

    // Build categorical legend
    const legendStops = [
      { color: 'rgba(244,67,54,0.9)', label: 'Historical (since 1900)' },
      { color: 'rgba(255,152,0,0.9)', label: 'Holocene' },
    ]
    const hasPleistocene = features.some((f) => f.properties.activity === 'pleistocene')
    if (hasPleistocene) {
      legendStops.push({ color: 'rgba(158,158,158,0.7)', label: 'Pleistocene' })
    }

    this.legendCallback?.({
      visible: true,
      title: 'VOLCANOES',
      type: 'categorical',
      stops: legendStops,
    })
  }

  /**
   * Show world languages as native MapLibre circle points on the globe.
   * Colored by language family. Uses native circle layers (not Deck.gl) to
   * stay anchored to the globe surface at any tilt angle.
   *
   * @param {Array} features - GeoJSON Feature array with Point geometries
   * @param {Object} opts - { familyFilter, endangered }
   */
  showLanguages(features, opts = {}) {
    this.clearLayers()

    const map = this.globe.map
    const layerId = 'data-languages'
    const sourceId = `${layerId}-src`

    // Build GeoJSON FeatureCollection (features already have the right shape)
    const geojson = { type: 'FeatureCollection', features }

    // Family → color mapping for MapLibre data-driven styling
    // Top families get distinct colors; smaller families grouped
    const colorExpr = [
      'match',
      ['get', 'family'],
      'Indo-European',
      '#4285F4',
      'Sino-Tibetan',
      '#EA4335',
      'Niger-Congo',
      '#34A853',
      'Austronesian',
      '#FF9800',
      'Afro-Asiatic',
      '#9C27B0',
      'Dravidian',
      '#009688',
      'Turkic',
      '#FFC107',
      'Uralic',
      '#00BCD4',
      'Trans-New Guinea',
      '#E91E63',
      'Australian',
      '#8BC34A',
      'Tai-Kadai',
      '#795548',
      'Austroasiatic',
      '#607D8B',
      'Quechuan',
      '#FF5722',
      'Hmong-Mien',
      '#CDDC39',
      'Na-Dene',
      '#3F51B5',
      'Japonic',
      '#F44336',
      'Koreanic',
      '#2196F3',
      'Mongolic',
      '#FF6F00',
      'Eskimo-Aleut',
      '#B3E5FC',
      'Northeast Caucasian',
      '#CE93D8',
      'Northwest Caucasian',
      '#BA68C8',
      'Kartvelian',
      '#AB47BC',
      'Nilo-Saharan',
      '#66BB6A',
      'North American',
      '#5C6BC0',
      'South American',
      '#26A69A',
      'Sign Language',
      '#BDBDBD',
      'Creole',
      '#A1887F',
      '#9E9E9E', // fallback
    ]

    // Remove any existing language layers
    this.globe._removeNativeSync(layerId)

    map.addSource(sourceId, { type: 'geojson', data: geojson })

    // Main circle layer — color-coded by family
    // With 8000+ points, use smaller radii and no glow to avoid overdraw
    map.addLayer({
      id: `${layerId}-circle`,
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 1.5, 3, 2, 6, 3.5, 10, 6, 14, 10],
        'circle-color': colorExpr,
        'circle-stroke-color': 'rgba(0,0,0,0.3)',
        'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 0, 0, 4, 0.3, 8, 0.5],
        'circle-opacity': 0.85,
      },
    })

    this.globe._nativeLayerIds.add(layerId)

    // Click handler for popup
    this._languageClickHandler = (e) => {
      const feats = map.queryRenderedFeatures(e.point, { layers: [`${layerId}-circle`] })
      if (feats.length > 0 && this.globe._popup) {
        const p = feats[0].properties
        const statusBadge =
          p.status === 'endangered'
            ? '<span style="color:#ff1744;font-weight:bold"> — ENDANGERED</span>'
            : p.status === 'extinct'
              ? '<span style="color:#888;font-weight:bold"> — EXTINCT</span>'
              : p.status === 'vulnerable'
                ? '<span style="color:#FFA000;font-weight:bold"> — VULNERABLE</span>'
                : ''
        const rows = [
          `<div class="ev-popup-name">${p.name}${statusBadge}</div>`,
          `<div class="ev-popup-row"><span class="ev-popup-label">Family</span><span class="ev-popup-value">${p.family}</span></div>`,
          `<div class="ev-popup-row"><span class="ev-popup-label">Region</span><span class="ev-popup-value">${p.macroarea}</span></div>`,
          p.iso639_3
            ? `<div class="ev-popup-row"><span class="ev-popup-label">ISO 639-3</span><span class="ev-popup-value">${p.iso639_3}</span></div>`
            : '',
          `<div class="ev-popup-row"><span class="ev-popup-label">Glottocode</span><span class="ev-popup-value">${p.glottocode}</span></div>`,
        ].filter(Boolean)
        this.globe._popup.setLngLat(e.lngLat).setHTML(rows.join('')).addTo(map)
      }
    }
    map.on('click', `${layerId}-circle`, this._languageClickHandler)

    // Cursor change on hover
    map.on('mouseenter', `${layerId}-circle`, () => {
      map.getCanvas().style.cursor = 'pointer'
    })
    map.on('mouseleave', `${layerId}-circle`, () => {
      map.getCanvas().style.cursor = ''
    })

    // Build legend — show top families present in data
    const familyCounts = {}
    for (const f of features) {
      const fam = f.properties.family
      familyCounts[fam] = (familyCounts[fam] || 0) + 1
    }

    const FAMILY_HEX = {
      'Indo-European': '#4285F4',
      'Sino-Tibetan': '#EA4335',
      'Niger-Congo': '#34A853',
      Austronesian: '#FF9800',
      'Afro-Asiatic': '#9C27B0',
      Dravidian: '#009688',
      Turkic: '#FFC107',
      Uralic: '#00BCD4',
      'Trans-New Guinea': '#E91E63',
      Australian: '#8BC34A',
      'Tai-Kadai': '#795548',
      Austroasiatic: '#607D8B',
      Quechuan: '#FF5722',
      'Na-Dene': '#3F51B5',
      'North American': '#5C6BC0',
      'South American': '#26A69A',
      Japonic: '#F44336',
      Koreanic: '#2196F3',
    }

    // Show top 8 families in legend (plus "Other" if present)
    const topFamilies = Object.entries(familyCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)

    const legendStops = topFamilies.map(([fam, count]) => ({
      color: FAMILY_HEX[fam] || '#9E9E9E',
      label: `${fam} (${count})`,
    }))

    const otherCount = Object.entries(familyCounts)
      .filter(([fam]) => !topFamilies.find(([f]) => f === fam))
      .reduce((sum, [, c]) => sum + c, 0)
    if (otherCount > 0) {
      legendStops.push({ color: '#9E9E9E', label: `Other (${otherCount})` })
    }

    this.legendCallback?.({
      visible: true,
      title: 'WORLD LANGUAGES',
      type: 'categorical',
      stops: legendStops,
    })
  }

  /**
   * Show Joshua Project people groups as MapLibre native circle layers.
   * Points colored by people cluster and sized by population.
   *
   * @param {Array} features - GeoJSON Feature array with Point geometries
   */
  // ── People Groups color schemes ──────────────────────────────
  // Three switchable cartographic schemes for Joshua Project data.
  // Each defines a MapLibre `match` expression, a hex lookup for the
  // legend, and the property key to group by.

  static _PG_SCHEMES = [
    {
      id: 'affinity_bloc',
      title: 'AFFINITY BLOC',
      property: 'affinity_bloc',
      colorExpr: [
        'match',
        ['get', 'affinity_bloc'],
        'South Asian Peoples',
        '#FF9800',
        'Sub-Saharan Peoples',
        '#34A853',
        'Southeast Asian Peoples',
        '#4CAF50',
        'East Asian Peoples',
        '#EA4335',
        'Arab World',
        '#FFC107',
        'Turkic Peoples',
        '#9C27B0',
        'Persian-Median',
        '#E91E63',
        'Tibetan-Himalayan Peoples',
        '#00BCD4',
        'Malay Peoples',
        '#FF5722',
        'Eurasian Peoples',
        '#3F51B5',
        'Jewish',
        '#2196F3',
        'Latin-Caribbean Americans',
        '#8BC34A',
        'North American Peoples',
        '#5C6BC0',
        'Pacific Islanders',
        '#26A69A',
        'Deaf',
        '#BDBDBD',
        'Horn of Africa Peoples',
        '#795548',
        '#9E9E9E',
      ],
      hex: {
        'South Asian Peoples': '#FF9800',
        'Sub-Saharan Peoples': '#34A853',
        'Southeast Asian Peoples': '#4CAF50',
        'East Asian Peoples': '#EA4335',
        'Arab World': '#FFC107',
        'Turkic Peoples': '#9C27B0',
        'Persian-Median': '#E91E63',
        'Tibetan-Himalayan Peoples': '#00BCD4',
        'Malay Peoples': '#FF5722',
        'Eurasian Peoples': '#3F51B5',
        Jewish: '#2196F3',
        'Latin-Caribbean Americans': '#8BC34A',
        'North American Peoples': '#5C6BC0',
        'Pacific Islanders': '#26A69A',
        'Horn of Africa Peoples': '#795548',
        Deaf: '#BDBDBD',
      },
    },
    {
      id: 'primary_religion',
      title: 'PRIMARY RELIGION',
      property: 'primary_religion',
      colorExpr: [
        'match',
        ['get', 'primary_religion'],
        'Christianity',
        '#4FC3F7', // sky blue
        'Islam',
        '#66BB6A', // green
        'Hinduism',
        '#FF9800', // orange
        'Buddhism',
        '#FFEE58', // yellow
        'Ethnic Religions',
        '#A1887F', // warm brown
        'Non-Religious',
        '#B0BEC5', // blue-gray
        'Unknown',
        '#757575', // dark gray
        'Other / Small',
        '#CE93D8', // lavender
        '#9E9E9E',
      ],
      hex: {
        Christianity: '#4FC3F7',
        Islam: '#66BB6A',
        Hinduism: '#FF9800',
        Buddhism: '#FFEE58',
        'Ethnic Religions': '#A1887F',
        'Non-Religious': '#B0BEC5',
        Unknown: '#757575',
        'Other / Small': '#CE93D8',
      },
    },
    {
      id: 'jp_scale',
      title: 'REACH STATUS (JP SCALE)',
      property: 'jp_scale_text',
      colorExpr: [
        'match',
        ['get', 'jp_scale_text'],
        'Unreached',
        '#E53935', // red
        'Minimally Reached',
        '#FF7043', // deep orange
        'Superficially Reached',
        '#FFC107', // amber
        'Partially Reached',
        '#9CCC65', // light green
        'Significantly Reached',
        '#43A047', // green
        '#9E9E9E',
      ],
      hex: {
        Unreached: '#E53935',
        'Minimally Reached': '#FF7043',
        'Superficially Reached': '#FFC107',
        'Partially Reached': '#9CCC65',
        'Significantly Reached': '#43A047',
      },
    },
  ]

  showPeopleGroups(features) {
    this.clearLayers()

    const map = this.globe.map
    const layerId = 'data-people-groups'
    const sourceId = `${layerId}-src`
    const geojson = { type: 'FeatureCollection', features }

    // Store features + default scheme for switching later
    this._pgFeatures = features
    this._pgSchemeIndex = 0

    // Remove any existing people group layers (including cluster sublayers)
    this.globe._removeNativeSync(layerId)

    // ── Clustered GeoJSON source ─────────────────────────────
    map.addSource(sourceId, {
      type: 'geojson',
      data: geojson,
      cluster: true,
      clusterRadius: 50,
      clusterMaxZoom: 12,
    })

    // ── Cluster circles (aggregated bubbles) ─────────────────
    map.addLayer({
      id: `${layerId}-clusters`,
      type: 'circle',
      source: sourceId,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'interpolate',
          ['linear'],
          ['get', 'point_count'],
          2,
          'rgba(245,166,35,0.55)', // amber (few)
          20,
          'rgba(245,124,0,0.65)', // deep orange
          100,
          'rgba(230,74,25,0.75)', // burnt orange (many)
          500,
          'rgba(183,28,28,0.80)', // dark red (very dense)
        ],
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['get', 'point_count'],
          2,
          12,
          10,
          16,
          50,
          22,
          200,
          30,
          1000,
          40,
        ],
        'circle-stroke-color': 'rgba(255,255,255,0.35)',
        'circle-stroke-width': 1,
      },
    })

    // ── Cluster count labels ─────────────────────────────────
    map.addLayer({
      id: `${layerId}-cluster-count`,
      type: 'symbol',
      source: sourceId,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Open Sans Bold'],
        'text-size': 11,
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': '#ffffff',
      },
    })

    // ── Unclustered individual points ────────────────────────
    const scheme = LayerManager._PG_SCHEMES[0]
    const sizeExpr = [
      'interpolate',
      ['linear'],
      ['zoom'],
      0,
      [
        'interpolate',
        ['linear'],
        ['coalesce', ['to-number', ['get', 'population'], 0], 0],
        0,
        1.5,
        1000,
        2,
        100000,
        3,
        1000000,
        4,
        10000000,
        5,
      ],
      6,
      [
        'interpolate',
        ['linear'],
        ['coalesce', ['to-number', ['get', 'population'], 0], 0],
        0,
        2,
        1000,
        3,
        100000,
        5,
        1000000,
        7,
        10000000,
        10,
      ],
      14,
      [
        'interpolate',
        ['linear'],
        ['coalesce', ['to-number', ['get', 'population'], 0], 0],
        0,
        4,
        1000,
        6,
        100000,
        9,
        1000000,
        12,
        10000000,
        16,
      ],
    ]

    map.addLayer({
      id: `${layerId}-circle`,
      type: 'circle',
      source: sourceId,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': sizeExpr,
        'circle-color': scheme.colorExpr,
        'circle-stroke-color': 'rgba(0,0,0,0.3)',
        'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 0, 0, 4, 0.3, 8, 0.5],
        'circle-opacity': 0.85,
      },
    })

    this.globe._nativeLayerIds.add(layerId)

    // ── Helper: format population number ──
    const fmtPop = (n) => {
      const v = parseInt(n, 10) || 0
      return v >= 1e6
        ? `${(v / 1e6).toFixed(1)}M`
        : v >= 1e3
          ? `${(v / 1e3).toFixed(1)}K`
          : String(v)
    }

    // ── Click cluster: show summary popup with key stats ──
    this._peopleGroupClusterClickHandler = (e) => {
      const clusterFeats = map.queryRenderedFeatures(e.point, { layers: [`${layerId}-clusters`] })
      if (clusterFeats.length === 0 || !this.globe._popup) return

      const cluster = clusterFeats[0]
      const clusterId = cluster.properties.cluster_id
      const pointCount = cluster.properties.point_count
      const coords = cluster.geometry.coordinates
      const src = map.getSource(sourceId)

      // Fetch up to 200 leaves to compute stats (covers most clusters)
      src.getClusterLeaves(clusterId, 200, 0, (err, leaves) => {
        if (err || !leaves?.length) return

        // ── Compute summary stats ──
        const religions = {}
        const countries = {}
        let totalPop = 0
        let unreachedCount = 0

        for (const f of leaves) {
          const p = f.properties
          const rel = p.primary_religion || 'Unknown'
          religions[rel] = (religions[rel] || 0) + 1
          const ctry = p.country || 'Unknown'
          countries[ctry] = (countries[ctry] || 0) + 1
          totalPop += parseInt(p.population, 10) || 0
          if (parseInt(p.jp_scale, 10) <= 2) unreachedCount++
        }

        // Top 3 religions
        const topReligions = Object.entries(religions)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([r, n]) => `${r} (${n})`)
          .join(', ')

        // Top 3 countries (if multi-country cluster)
        const countryEntries = Object.entries(countries).sort((a, b) => b[1] - a[1])
        const countryLabel =
          countryEntries.length === 1
            ? countryEntries[0][0]
            : countryEntries
                .slice(0, 3)
                .map(([c, n]) => `${c} (${n})`)
                .join(', ')

        // Largest groups by population
        const topGroups = [...leaves]
          .sort(
            (a, b) =>
              (parseInt(b.properties.population, 10) || 0) -
              (parseInt(a.properties.population, 10) || 0)
          )
          .slice(0, 3)
          .map((f) => `${f.properties.name} (${fmtPop(f.properties.population)})`)
          .join(', ')

        const unreachedPct = Math.round((unreachedCount / leaves.length) * 100)

        // ── Build popup HTML ──
        const rows = [
          `<div class="ev-popup-name">${pointCount} People Groups</div>`,
          `<div class="ev-popup-row"><span class="ev-popup-label">Location</span><span class="ev-popup-value">${countryLabel}</span></div>`,
          `<div class="ev-popup-row"><span class="ev-popup-label">Total Pop.</span><span class="ev-popup-value">${fmtPop(totalPop)}</span></div>`,
          `<div class="ev-popup-row"><span class="ev-popup-label">Religions</span><span class="ev-popup-value">${topReligions}</span></div>`,
          `<div class="ev-popup-row"><span class="ev-popup-label">Unreached</span><span class="ev-popup-value">${unreachedCount} (${unreachedPct}%)</span></div>`,
          `<div class="ev-popup-row"><span class="ev-popup-label">Largest</span><span class="ev-popup-value">${topGroups}</span></div>`,
          `<div class="ev-popup-zoom" data-cluster-id="${clusterId}" style="margin-top:8px;text-align:center;cursor:pointer;color:#F5A623;font-size:12px;font-weight:600;letter-spacing:0.5px;padding:4px 0;">▼ ZOOM IN ▼</div>`,
        ]

        this.globe._popup.setLngLat(coords).setHTML(rows.join('')).addTo(map)

        // Attach zoom-in click to the "ZOOM IN" link
        const zoomEl = document.querySelector('.ev-popup-zoom')
        if (zoomEl) {
          zoomEl.addEventListener('click', () => {
            this.globe._popup.remove()
            src.getClusterExpansionZoom(clusterId, (err2, zoom) => {
              if (err2) return
              map.easeTo({ center: coords, zoom: zoom + 0.5, duration: 500 })
            })
          })
        }
      })
    }

    // ── Click individual point: show detail popup ──
    this._peopleGroupPointClickHandler = (e) => {
      const feats = map.queryRenderedFeatures(e.point, { layers: [`${layerId}-circle`] })
      if (feats.length === 0 || !this.globe._popup) return

      const p = feats[0].properties
      const jpBadge =
        parseInt(p.jp_scale, 10) <= 2
          ? '<span style="color:#ff1744;font-weight:bold"> — UNREACHED</span>'
          : ''
      const indigenousBadge =
        p.is_indigenous === true || p.is_indigenous === 'true'
          ? '<span style="color:#009688;font-weight:bold"> — INDIGENOUS</span>'
          : ''
      const rows = [
        `<div class="ev-popup-name">${p.name}${jpBadge}${indigenousBadge}</div>`,
        `<div class="ev-popup-row"><span class="ev-popup-label">Country</span><span class="ev-popup-value">${p.country}</span></div>`,
        `<div class="ev-popup-row"><span class="ev-popup-label">Cluster</span><span class="ev-popup-value">${p.people_cluster}</span></div>`,
        `<div class="ev-popup-row"><span class="ev-popup-label">Population</span><span class="ev-popup-value">${fmtPop(p.population)}</span></div>`,
        `<div class="ev-popup-row"><span class="ev-popup-label">Language</span><span class="ev-popup-value">${p.primary_language}</span></div>`,
        `<div class="ev-popup-row"><span class="ev-popup-label">Religion</span><span class="ev-popup-value">${p.primary_religion}</span></div>`,
        p.jp_scale_text
          ? `<div class="ev-popup-row"><span class="ev-popup-label">JP Scale</span><span class="ev-popup-value">${p.jp_scale_text}</span></div>`
          : '',
      ].filter(Boolean)
      this.globe._popup.setLngLat(e.lngLat).setHTML(rows.join('')).addTo(map)
    }

    // ── Double-click cluster: zoom directly without popup ──
    this._peopleGroupDblClickHandler = (e) => {
      const clusterFeats = map.queryRenderedFeatures(e.point, { layers: [`${layerId}-clusters`] })
      if (clusterFeats.length > 0) {
        e.preventDefault() // prevent default map double-click zoom
        const clusterId = clusterFeats[0].properties.cluster_id
        const coords = clusterFeats[0].geometry.coordinates
        this.globe._popup?.remove()
        map.getSource(sourceId).getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return
          map.easeTo({ center: coords, zoom: zoom + 0.5, duration: 500 })
        })
      }
    }

    map.on('click', `${layerId}-clusters`, this._peopleGroupClusterClickHandler)
    map.on('click', `${layerId}-circle`, this._peopleGroupPointClickHandler)
    map.on('dblclick', `${layerId}-clusters`, this._peopleGroupDblClickHandler)

    // Cursor change on hover for both clusters and individual points
    const setCursor = () => {
      map.getCanvas().style.cursor = 'pointer'
    }
    const resetCursor = () => {
      map.getCanvas().style.cursor = ''
    }
    map.on('mouseenter', `${layerId}-clusters`, setCursor)
    map.on('mouseleave', `${layerId}-clusters`, resetCursor)
    map.on('mouseenter', `${layerId}-circle`, setCursor)
    map.on('mouseleave', `${layerId}-circle`, resetCursor)

    // Render legend for the initial scheme (with pagination metadata)
    this._updatePeopleGroupLegend()
  }

  /**
   * Switch the people groups color scheme without reloading data.
   * Called by the legend pagination arrows.
   * @param {number} index - Scheme index (0 = affinity, 1 = religion, 2 = JP scale)
   */
  switchPeopleGroupScheme(index) {
    const schemes = LayerManager._PG_SCHEMES
    if (!this._pgFeatures) return
    this._pgSchemeIndex = ((index % schemes.length) + schemes.length) % schemes.length

    const map = this.globe.map
    const layerId = 'data-people-groups'
    const scheme = schemes[this._pgSchemeIndex]

    // Update the circle paint for unclustered points
    if (map.getLayer(`${layerId}-circle`)) {
      map.setPaintProperty(`${layerId}-circle`, 'circle-color', scheme.colorExpr)
    }

    // Update legend
    this._updatePeopleGroupLegend()
  }

  /** Build and emit the people-group legend for the active scheme. */
  _updatePeopleGroupLegend() {
    const schemes = LayerManager._PG_SCHEMES
    const scheme = schemes[this._pgSchemeIndex]
    const features = this._pgFeatures || []

    // Count occurrences of the active property
    const counts = {}
    for (const f of features) {
      const v = f.properties[scheme.property] || 'Other'
      counts[v] = (counts[v] || 0) + 1
    }

    const top = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)

    const stops = top.map(([val, count]) => ({
      color: scheme.hex[val] || '#9E9E9E',
      label: `${val} (${count})`,
    }))

    const otherCount = Object.entries(counts)
      .filter(([v]) => !top.find(([tv]) => tv === v))
      .reduce((sum, [, n]) => sum + n, 0)
    if (otherCount > 0) {
      stops.push({ color: '#9E9E9E', label: `Other (${otherCount})` })
    }

    this.legendCallback?.({
      visible: true,
      title: 'PEOPLE GROUPS (JOSHUA PROJECT)',
      type: 'categorical',
      stops,
      // Pagination metadata for the legend UI
      pagination: {
        current: this._pgSchemeIndex,
        total: schemes.length,
        label: scheme.title,
        onSwitch: (idx) => this.switchPeopleGroupScheme(idx),
      },
    })
  }

  /**
   * Show world religions as a categorical choropleth on country polygons.
   * Countries colored by dominant religion using MapLibre native fill layers.
   *
   * @param {object} geojson - GeoJSON FeatureCollection with religion_code property
   */
  showReligionMap(geojson) {
    this.clearLayers()

    if (!geojson?.features?.length) return

    console.log(`[layers] showReligionMap: ${geojson.features.length} features`)

    this._activeChoropleth = { attribute: 'dominant_religion', displayName: 'Dominant Religion' }
    this.globe._activeChoropleth = this._activeChoropleth

    // Color map for religion codes
    const CODE_META = {
      CHR: { color: '#4285F4', label: 'Christianity' },
      ISL: { color: '#34A853', label: 'Islam' },
      HIN: { color: '#FF9800', label: 'Hinduism' },
      BUD: { color: '#FFC107', label: 'Buddhism' },
      JUD: { color: '#3F51B5', label: 'Judaism' },
      FOL: { color: '#009688', label: 'Folk / Indigenous' },
      UNF: { color: '#9E9E9E', label: 'Unaffiliated' },
      OTH: { color: '#9C27B0', label: 'Other' },
      IND: { color: '#795548', label: 'Indigenous' },
    }

    // Stamp _fill on each feature from its religion_code
    const codeCounts = {}
    for (const f of geojson.features) {
      const code = f.properties.religion_code
      f.properties._fill = CODE_META[code]?.color || '#555555'
      if (code) codeCounts[code] = (codeCounts[code] || 0) + 1
    }

    // Build legend from what's actually in the data
    const legendStops = Object.entries(codeCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([code, count]) => ({
        color: CODE_META[code]?.color || '#555',
        label: `${CODE_META[code]?.label || code} (${count})`,
      }))

    const legendConfig = {
      visible: true,
      title: 'WORLD RELIGIONS',
      type: 'categorical',
      stops: legendStops,
    }

    // Route through ChoroplethStyleManager for extrusion + mode switcher support
    this.choroplethStyleManager.setPreclassifiedData({
      enrichedGeojson: geojson,
      attribute: null, // categorical — uses fixed height for extrusion
      linePaint: { 'line-color': 'rgba(255,255,255,0.5)', 'line-width': 0.8 },
      legendConfig,
    })
  }

  /**
   * Show religious or ethnic diversity index as a gradient choropleth.
   * Uses a yellow-to-red gradient where darker = more diverse.
   *
   * @param {object} geojson - GeoJSON FeatureCollection
   * @param {string} attribute - 'religious_diversity_index' or 'ethnic_diversity_index'
   * @param {string} title - Legend title
   */
  showReligionDiversity(geojson, attribute, title) {
    this.clearLayers()

    if (!geojson?.features?.length) return

    console.log(
      `[layers] showReligionDiversity: ${geojson.features.length} features, attr=${attribute}`
    )

    this._activeChoropleth = { attribute, displayName: title }
    this.globe._activeChoropleth = this._activeChoropleth

    // Gradient stops: light yellow (low diversity) → dark red (high diversity)
    const GRAD_STOPS = [
      [0.0, '#ffffcc'],
      [0.2, '#ffeda0'],
      [0.35, '#feb24c'],
      [0.5, '#fd8d3c'],
      [0.65, '#fc4e2a'],
      [0.8, '#e31a1c'],
      [1.0, '#800026'],
    ]

    // Stamp _fill on each feature using linear interpolation of gradient
    for (const f of geojson.features) {
      const v = Number(f.properties?.[attribute]) || 0
      // Find surrounding stops
      let lo = GRAD_STOPS[0]
      let hi = GRAD_STOPS[GRAD_STOPS.length - 1]
      for (let i = 0; i < GRAD_STOPS.length - 1; i++) {
        if (v >= GRAD_STOPS[i][0] && v <= GRAD_STOPS[i + 1][0]) {
          lo = GRAD_STOPS[i]
          hi = GRAD_STOPS[i + 1]
          break
        }
      }
      // Linear interpolate between lo and hi hex colors
      const t = hi[0] === lo[0] ? 0 : (v - lo[0]) / (hi[0] - lo[0])
      const hexToRgb = (hex) => [
        parseInt(hex.slice(1, 3), 16),
        parseInt(hex.slice(3, 5), 16),
        parseInt(hex.slice(5, 7), 16),
      ]
      const rgbToHex = (r, g, b) =>
        '#' + [r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')
      const [r1, g1, b1] = hexToRgb(lo[1])
      const [r2, g2, b2] = hexToRgb(hi[1])
      f.properties._fill = rgbToHex(r1 + (r2 - r1) * t, g1 + (g2 - g1) * t, b1 + (b2 - b1) * t)
    }

    // Gradient legend
    const legendConfig = {
      visible: true,
      title,
      type: 'gradient',
      stops: [
        { value: 0, color: '#ffffcc', label: '0 (Homogeneous)' },
        { value: 0.25, color: '#feb24c', label: '0.25' },
        { value: 0.5, color: '#fd8d3c', label: '0.50' },
        { value: 0.75, color: '#e31a1c', label: '0.75' },
        { value: 1.0, color: '#800026', label: '1.0 (Most Diverse)' },
      ],
    }

    // Route through ChoroplethStyleManager for extrusion + mode switcher support
    this.choroplethStyleManager.setPreclassifiedData({
      enrichedGeojson: geojson,
      attribute,
      linePaint: { 'line-color': 'rgba(255,255,255,0.5)', 'line-width': 0.8 },
      legendConfig,
    })
  }

  /**
   * Show ocean currents as MapLibre line layers with warm/cold color coding
   * and arrow markers along the line direction.
   *
   * @param {Object} geojson - GeoJSON FeatureCollection of LineString features
   */
  showOceanCurrents(geojson) {
    this.clearLayers()

    if (!geojson?.features?.length) return

    const map = this.globe.map
    const layerId = 'data-ocean-currents'
    const sourceId = `${layerId}-src`

    console.log(`[layers] showOceanCurrents: ${geojson.features.length} currents`)

    // Guard: wait for style to finish loading before adding native layers
    if (!map.isStyleLoaded()) {
      console.log('[layers] Style not loaded yet — deferring showOceanCurrents')
      map.once('idle', () => this.showOceanCurrents(geojson))
      return
    }

    this.globe._removeNativeSync(layerId)

    map.addSource(sourceId, { type: 'geojson', data: geojson })

    // ── Glow halo layer (wider, semi-transparent) ─────────────────
    map.addLayer({
      id: `${layerId}-glow`,
      type: 'line',
      source: sourceId,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': [
          'match',
          ['get', 'current_type'],
          'warm',
          'rgba(244, 67, 54, 0.25)',
          'cold',
          'rgba(33, 150, 243, 0.25)',
          'rgba(150, 150, 150, 0.25)',
        ],
        'line-width': [
          'interpolate',
          ['linear'],
          ['get', 'speed_knots'],
          0.3,
          8,
          1.0,
          14,
          2.0,
          20,
          4.0,
          28,
        ],
        'line-blur': 3,
      },
    })

    // ── Main line layer ───────────────────────────────────────────
    map.addLayer({
      id: `${layerId}-line`,
      type: 'line',
      source: sourceId,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': [
          'match',
          ['get', 'current_type'],
          'warm',
          '#F44336',
          'cold',
          '#2196F3',
          '#9E9E9E',
        ],
        'line-width': [
          'interpolate',
          ['linear'],
          ['get', 'speed_knots'],
          0.3,
          2,
          1.0,
          3,
          2.0,
          4.5,
          4.0,
          6,
        ],
        'line-opacity': 0.85,
      },
    })

    // ── Arrow markers along lines (symbol layer) ──────────────────
    map.addLayer({
      id: `${layerId}-arrows`,
      type: 'symbol',
      source: sourceId,
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 100,
        'text-field': '\u25B6',
        'text-size': 14,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-rotation-alignment': 'map',
        'text-keep-upright': false,
      },
      paint: {
        'text-color': [
          'match',
          ['get', 'current_type'],
          'warm',
          '#FFCDD2',
          'cold',
          '#BBDEFB',
          '#E0E0E0',
        ],
        'text-halo-color': 'rgba(0,0,0,0.5)',
        'text-halo-width': 0.5,
      },
    })

    // ── Name labels ───────────────────────────────────────────────
    map.addLayer({
      id: `${layerId}-labels`,
      type: 'symbol',
      source: sourceId,
      layout: {
        'symbol-placement': 'line-center',
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 12,
        'text-allow-overlap': false,
        'text-max-angle': 30,
        'text-offset': [0, -1.2],
      },
      paint: {
        'text-color': '#E0E0E0',
        'text-halo-color': 'rgba(0,0,0,0.9)',
        'text-halo-width': 1.5,
      },
    })

    // ── Hit target for click popups ───────────────────────────────
    map.addLayer({
      id: `${layerId}-hit`,
      type: 'line',
      source: sourceId,
      layout: { 'line-cap': 'round' },
      paint: { 'line-color': 'rgba(0,0,0,0)', 'line-width': 16, 'line-opacity': 0 },
    })

    // ── Click popup ──────────────────────────────────────────────
    map.on('mouseenter', `${layerId}-hit`, () => {
      map.getCanvas().style.cursor = 'pointer'
    })
    map.on('mouseleave', `${layerId}-hit`, () => {
      map.getCanvas().style.cursor = ''
    })
    map.on('click', `${layerId}-hit`, (e) => {
      const f = map.queryRenderedFeatures(e.point, { layers: [`${layerId}-hit`] })[0]
      if (!f) return
      const p = f.properties
      const typeColor = p.current_type === 'warm' ? '#F44336' : '#2196F3'
      const typeLabel = p.current_type === 'warm' ? 'WARM' : 'COLD'

      new maplibregl.Popup({ className: 'ev-popup', maxWidth: '280px' })
        .setLngLat(e.lngLat)
        .setHTML(
          `
          <div style="font-family: 'IBM Plex Sans', sans-serif; font-size: 13px; line-height: 1.6;">
            <div style="font-weight: 700; color: ${typeColor}; font-size: 14px;">${p.name} \u2014 ${typeLabel}</div>
            <table style="width:100%; margin-top:4px; border-collapse: collapse;">
              <tr><td style="color:#aaa; padding:2px 8px 2px 0;">Ocean</td><td style="font-weight:600; text-align:right;">${p.ocean}</td></tr>
              <tr><td style="color:#aaa; padding:2px 8px 2px 0;">Speed</td><td style="font-weight:600; text-align:right;">${p.speed_knots} knots</td></tr>
            </table>
            <div style="margin-top:6px; color:#bbb; font-size:11px; line-height:1.4;">${p.description || ''}</div>
          </div>
        `
        )
        .addTo(map)
    })

    this.globe._nativeLayerIds.add(layerId)

    // ── Categorical legend ────────────────────────────────────────
    this.legendCallback?.({
      visible: true,
      title: 'OCEAN CURRENTS',
      type: 'categorical',
      stops: [
        { color: [244, 67, 54, 230], label: 'Warm current' },
        { color: [33, 150, 243, 230], label: 'Cold current' },
      ],
    })
  }
}

/**
 * POI category → color mapping for Overture Maps places
 */
const POI_CATEGORY_COLORS = {
  university: [66, 133, 244, 220],
  college: [66, 133, 244, 220],
  school: [100, 160, 244, 220],
  hospital: [234, 67, 53, 220],
  medical_center: [234, 67, 53, 220],
  clinic: [234, 100, 90, 220],
  airport: [156, 39, 176, 220],
  train_station: [255, 152, 0, 220],
  bus_station: [255, 152, 0, 220],
  ferry_terminal: [255, 152, 0, 220],
  museum: [0, 150, 136, 220],
  library: [0, 150, 136, 220],
  art_gallery: [0, 150, 136, 220],
  place_of_worship: [255, 193, 7, 220],
  mosque: [255, 193, 7, 220],
  church: [255, 193, 7, 220],
  temple: [255, 193, 7, 220],
  synagogue: [255, 193, 7, 220],
  national_park: [76, 175, 80, 220],
  nature_reserve: [76, 175, 80, 220],
  zoo: [76, 175, 80, 220],
  botanical_garden: [76, 175, 80, 220],
  embassy: [26, 35, 126, 220],
  consulate: [26, 35, 126, 220],
  port: [0, 188, 212, 220],
  marina: [0, 188, 212, 220],
  power_plant: [255, 111, 0, 220],
  power_station: [255, 111, 0, 220],
  stadium: [233, 30, 99, 220],
  sports_complex: [233, 30, 99, 220],
  shopping_mall: [255, 111, 0, 220],
  market: [255, 111, 0, 220],
  cafe: [139, 90, 43, 220],
  coffee_shop: [139, 90, 43, 220],
  tea_room: [139, 110, 60, 220],
  bubble_tea: [139, 110, 60, 220],
  restaurant: [255, 87, 34, 220],
  fast_food_restaurant: [255, 87, 34, 220],
  hotel: [63, 81, 181, 220],
  accommodation: [63, 81, 181, 220],
  bed_and_breakfast: [63, 81, 181, 220],
  hostel: [63, 81, 181, 220],
  resort: [63, 81, 181, 220],
  bar: [156, 39, 176, 220],
  pub: [156, 39, 176, 220],
  cocktail_bar: [156, 39, 176, 220],
  bakery: [255, 167, 38, 220],
  pharmacy: [0, 150, 136, 220],
  drugstore: [0, 150, 136, 220],
  bank_credit_union: [0, 121, 107, 220],
  banks: [0, 121, 107, 220],
  supermarket: [76, 175, 80, 220],
  grocery_store: [76, 175, 80, 220],
  gas_station: [255, 152, 0, 220],
  gym: [233, 30, 99, 220],
  cinema: [121, 85, 72, 220],
  post_office: [33, 150, 243, 220],
  fire_department: [234, 67, 53, 220],
  police_department: [33, 150, 243, 220],
  beach: [255, 213, 79, 220],
  campground: [76, 175, 80, 220],
  landmark_and_historical_building: [121, 85, 72, 220],
  monument: [121, 85, 72, 220],
  castle: [121, 85, 72, 220],
  palace: [121, 85, 72, 220],
  lighthouse: [255, 213, 79, 220],
  church_cathedral: [255, 193, 7, 220],
  catholic_church: [255, 193, 7, 220],
  baptist_church: [255, 193, 7, 220],
  hindu_temple: [255, 193, 7, 220],
  buddhist_temple: [255, 193, 7, 220],
  winery: [128, 0, 128, 220],
  brewery: [255, 152, 0, 220],
  bookstore: [0, 150, 136, 220],
  veterinarian: [76, 175, 80, 220],
  dentist: [0, 150, 136, 220],
  doctor: [234, 67, 53, 220],
  golf_course: [76, 175, 80, 220],
  swimming_pool: [33, 150, 243, 220],
  amusement_park: [233, 30, 99, 220],
  aquarium: [33, 150, 243, 220],
  ski_resort: [100, 181, 246, 220],
  default: [158, 158, 158, 220],
}

function formatCategoryName(cat) {
  const names = {
    university: 'University',
    college: 'College',
    school: 'School',
    hospital: 'Hospital',
    medical_center: 'Medical Center',
    clinic: 'Clinic',
    airport: 'Airport',
    train_station: 'Train Station',
    bus_station: 'Bus Station',
    ferry_terminal: 'Ferry Terminal',
    museum: 'Museum',
    library: 'Library',
    art_gallery: 'Art Gallery',
    place_of_worship: 'Place of Worship',
    mosque: 'Mosque',
    church: 'Church',
    temple: 'Temple',
    synagogue: 'Synagogue',
    national_park: 'National Park',
    nature_reserve: 'Nature Reserve',
    zoo: 'Zoo',
    botanical_garden: 'Botanical Garden',
    embassy: 'Embassy',
    consulate: 'Consulate',
    port: 'Port',
    marina: 'Marina',
    power_plant: 'Power Plant',
    power_station: 'Power Station',
    stadium: 'Stadium',
    sports_complex: 'Sports Complex',
    shopping_mall: 'Shopping Mall',
    market: 'Market',
    cafe: 'Café',
    coffee_shop: 'Coffee Shop',
    tea_room: 'Tea Room',
    bubble_tea: 'Bubble Tea',
    restaurant: 'Restaurant',
    fast_food_restaurant: 'Fast Food',
    hotel: 'Hotel',
    accommodation: 'Accommodation',
    bed_and_breakfast: 'B&B',
    hostel: 'Hostel',
    resort: 'Resort',
    bar: 'Bar',
    pub: 'Pub',
    cocktail_bar: 'Cocktail Bar',
    wine_bar: 'Wine Bar',
    beer_bar: 'Beer Bar',
    sports_bar: 'Sports Bar',
    bakery: 'Bakery',
    pharmacy: 'Pharmacy',
    drugstore: 'Drugstore',
    bank_credit_union: 'Bank',
    banks: 'Bank',
    credit_union: 'Credit Union',
    supermarket: 'Supermarket',
    grocery_store: 'Grocery Store',
    convenience_store: 'Convenience Store',
    gas_station: 'Gas Station',
    gym: 'Gym',
    yoga_studio: 'Yoga Studio',
    cinema: 'Cinema',
    post_office: 'Post Office',
    fire_department: 'Fire Department',
    police_department: 'Police Department',
    beach: 'Beach',
    campground: 'Campground',
    landmark_and_historical_building: 'Landmark',
    monument: 'Monument',
    castle: 'Castle',
    palace: 'Palace',
    lighthouse: 'Lighthouse',
    church_cathedral: 'Church',
    catholic_church: 'Catholic Church',
    baptist_church: 'Baptist Church',
    hindu_temple: 'Hindu Temple',
    buddhist_temple: 'Buddhist Temple',
    winery: 'Winery',
    brewery: 'Brewery',
    bookstore: 'Bookstore',
    veterinarian: 'Veterinarian',
    dentist: 'Dentist',
    doctor: 'Doctor',
    golf_course: 'Golf Course',
    swimming_pool: 'Swimming Pool',
    amusement_park: 'Amusement Park',
    aquarium: 'Aquarium',
    ski_resort: 'Ski Resort',
    college_university: 'University',
    elementary_school: 'Elementary School',
    middle_school: 'Middle School',
    high_school: 'High School',
  }
  // Guard: rows from analytical queries (proximity, center) may carry
  // categories not in the map — or none at all. Never crash the render.
  return names[cat] || String(cat || 'other').replace(/_/g, ' ')
}

function formatAttributeName(attr) {
  const names = {
    population: 'Population',
    POP_EST: 'Population',
    pop_est: 'Population',
    GDP_MD: 'GDP (Millions USD)',
    gdp_md: 'GDP (Millions USD)',
    POP_MAX: 'Population',
    POP_MIN: 'Min Population',
    INCOME_GRP: 'Income Group',
    income_grp: 'Income Group',
    CONTINENT: 'Continent',
    continent: 'Continent',
    SUBREGION: 'Sub-region',
    subregion: 'Sub-region',
    ECONOMY: 'Economy',
    economy: 'Economy',
    NAME: 'Name',
    name: 'Name',
    // World Bank indicators
    pop_growth_pct: 'Pop. Growth %',
    urban_pct: 'Urban %',
    gdp_usd: 'GDP (USD)',
    gdp_per_capita: 'GDP per Capita',
    gini_index: 'Gini Index',
    unemployment_pct: 'Unemployment %',
    literacy_rate: 'Literacy Rate %',
    primary_enrollment: 'Primary Enrollment %',
    secondary_enrollment: 'Secondary Enrollment %',
    life_expectancy: 'Life Expectancy',
    infant_mortality: 'Infant Mortality',
    physicians_per_1k: 'Physicians/1K',
    health_spending_pct: 'Health Spending %',
    forest_area_pct: 'Forest Area %',
    co2_per_capita: 'CO₂ per Capita',
    renewable_energy_pct: 'Renewable Energy %',
    electricity_access_pct: 'Electricity Access %',
    internet_users_pct: 'Internet Users %',
    mobile_subscriptions: 'Mobile Subs/100',
    density_per_km2: 'Density/km²',
    area_km2: 'Area (km²)',
    hdi: 'Human Development Index',
    // NOTE: 'status' is defined once, below (Glottolog block) — JS object keys
    // must be unique; the later generic 'Status' label was already winning at
    // runtime, so the duplicate 'Freedom Status' entry was removed (no-dupe-keys).
    aggregate_score: 'Freedom Score',
    pr_score: 'Political Rights',
    cl_score: 'Civil Liberties',
    // Fragile States Index
    fsi_total: 'FSI Total Score',
    fsi_rank: 'FSI Rank',
    fsi_category: 'FSI Category',
    security_apparatus: 'Security Apparatus',
    factionalized_elites: 'Factionalized Elites',
    group_grievance: 'Group Grievance',
    economic_inequality: 'Economic Inequality',
    human_flight: 'Human Flight & Brain Drain',
    state_legitimacy: 'State Legitimacy',
    public_services: 'Public Services',
    human_rights: 'Human Rights',
    demographic_pressures: 'Demographic Pressures',
    refugees_idps: 'Refugees & IDPs',
    external_intervention: 'External Intervention',
    // FAOSTAT Agriculture
    wheat_production_mt: 'Wheat Production (Mt)',
    rice_production_mt: 'Rice Production (Mt)',
    maize_production_mt: 'Maize Production (Mt)',
    cereal_production_mt: 'Cereal Production (Mt)',
    cattle_million: 'Cattle (Millions)',
    sheep_million: 'Sheep (Millions)',
    chicken_million: 'Chickens (Millions)',
    arable_land_mha: 'Arable Land (Mha)',
    ag_land_pct: 'Agricultural Land (%)',
    fertilizer_use_kg_ha: 'Fertilizer Use (kg/ha)',
    irrigation_pct: 'Irrigated Land (%)',
    cereal_yield_kg_ha: 'Cereal Yield (kg/ha)',
    ag_exports_musd: 'Ag. Exports (M USD)',
    ag_imports_musd: 'Ag. Imports (M USD)',
    // UNESCO World Heritage Sites
    year_inscribed: 'Year Inscribed',
    endangered: 'Endangered',
    criteria: 'UNESCO Criteria',
    description: 'Description',
    // World Languages (Glottolog)
    glottocode: 'Glottocode',
    iso639_3: 'ISO 639-3',
    family: 'Language Family',
    macroarea: 'Macroarea',
    status: 'Status',
    // WHO Global Health Observatory
    life_expectancy_who: 'Life Expectancy (years)',
    under5_mortality: 'Under-5 Mortality (/1,000)',
    maternal_mortality: 'Maternal Mortality (/100k)',
    ncd_mortality_pct: 'NCD Mortality 30-70 (%)',
    malaria_incidence: 'Malaria Incidence (/1,000)',
    hiv_prevalence: 'HIV Prevalence 15-49 (%)',
    tb_incidence: 'TB Incidence (/100k)',
    dtp3_coverage: 'DTP3 Coverage (%)',
    measles_coverage: 'Measles MCV1 Coverage (%)',
    physicians_per_10k: 'Physicians (/10,000)',
    nurses_per_10k: 'Nurses & Midwives (/10,000)',
    skilled_birth_pct: 'Skilled Birth Attendance (%)',
    obesity_pct: 'Obesity BMI≥30 (%)',
    alcohol_per_capita: 'Alcohol per Capita (L)',
    tobacco_smoking_pct: 'Tobacco Smoking (%)',
    safe_sanitation_pct: 'Safe Sanitation (%)',
    safe_water_pct: 'Safe Drinking Water (%)',
    child_wasting_pct: 'Child Wasting <5 (%)',
    child_stunting_pct: 'Child Stunting <5 (%)',
    health_spending_usd: 'Health Spending/Capita (USD)',
    // FAO Aquastat — Water Resources
    water_stress_pct: 'Water Stress (%)',
    freshwater_per_capita: 'Freshwater/Capita (m³)',
    freshwater_total_km3: 'Total Freshwater (bn m³)',
    water_withdrawal_km3: 'Water Withdrawal (bn m³)',
    ag_water_pct: 'Agricultural Water Use (%)',
    industrial_water_pct: 'Industrial Water Use (%)',
    domestic_water_pct: 'Domestic Water Use (%)',
    irrigated_land_pct: 'Irrigated Land (%)',
    safe_drinking_water_pct: 'Safe Drinking Water (%)',
    basic_water_pct: 'Basic Water Access (%)',
    safe_sanitation_wb_pct: 'Safe Sanitation (%)',
    sdg_water_stress_pct: 'SDG 6.4.2 Water Stress (%)',
    // WTO Trade Statistics
    exports_usd: 'Merchandise Exports (US$)',
    imports_usd: 'Merchandise Imports (US$)',
    trade_balance_usd: 'Trade Balance (US$)',
    trade_pct_gdp: 'Trade Openness (% of GDP)',
    exports_pct_gdp: 'Exports (% of GDP)',
    imports_pct_gdp: 'Imports (% of GDP)',
    hightech_exports_pct: 'High-Tech Exports (%)',
    current_account_usd: 'Current Account Balance (US$)',
    service_exports_usd: 'Service Exports (US$)',
    // World Religions & Ethnicity
    dominant_religion: 'Dominant Religion',
    religion_code: 'Religion Code',
    christianity_pct: 'Christianity %',
    islam_pct: 'Islam %',
    hinduism_pct: 'Hinduism %',
    buddhism_pct: 'Buddhism %',
    folk_pct: 'Folk/Indigenous %',
    unaffiliated_pct: 'Unaffiliated %',
    other_pct: 'Other Religions %',
    religious_diversity_index: 'Religious Diversity',
    dominant_ethnicity: 'Dominant Ethnicity',
    ethnic_diversity_index: 'Ethnic Diversity',
  }
  return names[attr] || attr.replace(/_/g, ' ')
}

/** Convert [r, g, b, a] array to hex color string for MapLibre paint properties. */
function _rgbaToHex([r, g, b]) {
  return '#' + [r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')
}

export { LayerManager }
export default LayerManager
